import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import './App.css';


// ─── Shared auth context ────────────────────────────────────────────────────
const AuthContext = React.createContext(null);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
    });
    return () => subscription.unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, authLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

function useAuth() {
  return React.useContext(AuthContext);
}
// ────────────────────────────────────────────────────────────────────────────

function Navbar() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setMenuOpen(false);
    navigate('/');
  };

  return (
    <div>
      <nav style={styles.nav}>
        <div style={styles.navInner}>
        <Link to="/" style={styles.logo}>🏫 SchoolYardSale</Link>
          {/* Desktop nav */}
          <div style={styles.navLinks} className="desktop-nav">
            <Link to="/browse" style={styles.navLink}>Browse</Link>
            {user ? (
              <>
<Link to="/post" style={styles.navLink}>Post a Listing</Link>
<Link to="/my-listings" style={styles.navLink}>My Listings</Link>
<Link to="/saved" style={styles.navLink}>❤️ Saved</Link>
<Link to="/profile" style={styles.navLink}>Profile</Link>
                <button onClick={handleLogout} style={styles.navLinkOutline}>Log Out</button>
              </>
            ) : (
              <>
                <Link to="/login" style={styles.navLink}>Log In</Link>
                <Link to="/login" style={styles.navLinkButton}>Post a Listing</Link>
              </>
            )}
          </div>

          {/* Hamburger button */}
          <button
            style={styles.hamburger}
            className="hamburger"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <div style={styles.hamburgerLine}></div>
            <div style={styles.hamburgerLine}></div>
            <div style={styles.hamburgerLine}></div>
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {menuOpen && (
        <div style={styles.mobileMenu} className="mobile-menu">
          <Link to="/browse" style={styles.mobileMenuLink} onClick={() => setMenuOpen(false)}>Browse</Link>
          {user ? (
            <>
              <Link to="/post" style={styles.mobileMenuLink} onClick={() => setMenuOpen(false)}>Post a Listing</Link>
              <Link to="/my-listings" style={styles.mobileMenuLink} onClick={() => setMenuOpen(false)}>My Listings</Link>
              <Link to="/saved" style={styles.mobileMenuLink} onClick={() => setMenuOpen(false)}>❤️ Saved Listings</Link>
              <Link to="/profile" style={styles.mobileMenuLink} onClick={() => setMenuOpen(false)}>My Profile</Link>
              <button onClick={handleLogout} style={{...styles.mobileMenuLink, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 0}}>Log Out</button>
            </>
          ) : (
            <>
              <Link to="/login" style={styles.mobileMenuLink} onClick={() => setMenuOpen(false)}>Log In</Link>
              <Link to="/login" style={styles.mobileMenuLink} onClick={() => setMenuOpen(false)}>Post a Listing</Link>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Home () {
  return (
    <div>
      <div style={styles.hero}>
        <h1 style={{...styles.heroTitle, fontSize: window.innerWidth <= 768 ? '28px' : '42px'}}>Your School Community Marketplace</h1>
        <p style={styles.heroSubtitle}>
          Buy and sell textbooks, uniforms, and school gear with families at your school.
          No fees. No hassle. Just your community.
        </p>
        <div style={styles.heroButtons}>
          <Link to="/browse" style={styles.primaryButton}>Browse Listings</Link>
          <Link to="/post" style={styles.secondaryButton}>Post a Listing</Link>
        </div>
      </div>

      <div style={styles.categories}>
        <h2 style={styles.sectionTitle}>What can you find here?</h2>
        <div style={styles.categoryGrid}>
          <div style={styles.categoryCard}>
            <div style={styles.categoryIcon}>📚</div>
            <h3>Textbooks</h3>
            <p>Find the books you need for a fraction of the price.</p>
          </div>
          <div style={styles.categoryCard}>
            <div style={styles.categoryIcon}>👕</div>
            <h3>Uniforms & Clothing</h3>
            <p>School uniforms, logo apparel, and more from local families.</p>
          </div>
          <div style={styles.categoryCard}>
            <div style={styles.categoryIcon}>🎒</div>
            <h3>Gear & Supplies</h3>
            <p>Sports equipment, calculators, instruments, and other school supplies.</p>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', margin: '32px 0' }}>
        <AdBanner isMobile={false} />
      </div>

      <div style={styles.howItWorks}>
        <h2 style={styles.sectionTitle}>How it works</h2>
        <div style={styles.stepsGrid}>
          <div style={styles.step}>
            <div style={styles.stepNumber}>1</div>
            <h3>Find your school</h3>
            <p>Search by zip code to see listings from families at your school.</p>
          </div>
          <div style={styles.step}>
            <div style={styles.stepNumber}>2</div>
            <h3>Browse listings</h3>
            <p>Filter by category, condition, and price to find exactly what you need.</p>
          </div>
          <div style={styles.step}>
            <div style={styles.stepNumber}>3</div>
            <h3>Connect & exchange</h3>
            <p>Contact the seller directly and arrange a meetup. Simple as that.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

const getListingAge = (createdAt) => {
  const now = new Date();
  const created = new Date(createdAt);
  const diffMs = now - created;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  if (diffHours < 24) return '🆕 New Listing!';
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks !== 1 ? 's' : ''} ago`;
  return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`;
};

const getDistance = (lat1, lng1, lat2, lng2) => {
  const R = 3958.8; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const ads = [
  {
    headline: 'Get Amazon Prime',
    subline: 'Students & young adults: fast free shipping, Prime Video & more. As an Amazon Associate, SchoolYardSale earns from qualifying purchases.',
    url: 'https://www.amazon.com/amazonprime?tag=schoolyardsal-20',
    alt: 'Get Amazon Prime for students',
    image: '/ad-amazon-prime.svg',
  },
];

function AdBanner({ isMobile }) {
  const currentAd = ads[0];
  return (
    <div style={{ width: isMobile ? '100%' : '300px', flexShrink: 0 }}>
      <p style={{ fontSize: '11px', color: '#aaa', margin: '0 0 4px 0', textAlign: 'center' }}>Advertisement</p>
      <a href={currentAd.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', display: 'block' }}>
        {currentAd.image ? (
          <img src={currentAd.image} alt={currentAd.alt} style={{ width: '100%', borderRadius: '8px', display: 'block' }} />
        ) : (
          <div style={{ width: '100%', height: isMobile ? 'auto' : '250px', minHeight: '80px', backgroundColor: '#FF6B00', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px', boxSizing: 'border-box' }}>
            <p style={{ color: 'white', fontSize: isMobile ? '20px' : '26px', fontWeight: 'bold', textAlign: 'center', margin: 0 }}>{currentAd.headline}</p>
            <p style={{ color: 'white', fontSize: '13px', textAlign: 'center', marginTop: '10px', marginBottom: 0 }}>{currentAd.subline}</p>
          </div>
        )}
      </a>
    </div>
  );
}

function Browse() {
  const [selectedSchool, setSelectedSchool] = useState('');
  const [userSchools, setUserSchools] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const [userSchoolIds, setUserSchoolIds] = useState([]);
  const [radius, setRadius] = useState('');
  const [userLat, setUserLat] = useState(null);
  const [userLng, setUserLng] = useState(null);
  const [bookSuggestions, setBookSuggestions] = useState([]);
  const [filters, setFilters] = useState({
    category: '',
    condition: '',
    maxPrice: '',
    search: '',
    status: 'available',
    courseNumber: '',
    sort: 'newest',
  });
  const [courseNoBooklist, setCourseNoBooklist] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Fetch schools when user is available (handles OAuth late-arrival)
  useEffect(() => {
    if (!user) return;
    supabase.from('user_schools').select('school_id, schools(id, name)').eq('user_id', user.id)
      .then(({ data: schools }) => {
        if (schools) {
          setUserSchoolIds(schools.map(us => us.school_id));
          setUserSchools(schools.map(us => us.schools));
        }
      });
    supabase.from('profiles').select('lat, lng').eq('id', user.id).single()
      .then(({ data: profile }) => {
        if (profile) { setUserLat(profile.lat); setUserLng(profile.lng); }
      });
  }, [user]);

  useEffect(() => {
    if (user && userSchoolIds.length > 0) {
      fetchListings();
    }
  }, [user, userSchoolIds]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchSuggestions = async (query, category) => {
  if (!query) return setBookSuggestions([]);

  if (category === 'textbook') {
    try {
      const res = await fetch(`https://openlibrary.org/search.json?q=${encodeURIComponent(query)}&limit=3`);
      const data = await res.json();
      const books = (data.docs || []).slice(0, 3).map(book => ({
        type: 'book',
        title: book.title,
        author: book.author_name?.[0] || '',
        isbn: book.isbn?.[0] || '',
        coverId: book.cover_i || null,
      }));
      if (books.length > 0) {
        setBookSuggestions(books);
        return;
      }
    } catch (e) {}
  }

  setBookSuggestions([{ type: 'product', title: query }]);
};

const fetchListings = async () => {
    setLoading(true);
    setCourseNoBooklist(false);

    let query = supabase
      .from('listings')
      .select(`*, schools(name, city, zip_code, lat, lng)`)
      .in('status', filters.status === 'available' ? ['available'] : ['sold', 'pending'])
      .order(
        filters.sort === 'price_asc' || filters.sort === 'price_desc' ? 'price' : 'created_at',
        { ascending: filters.sort === 'oldest' || filters.sort === 'price_asc' }
      );

    if (selectedSchool === 'all') {
      // no school restriction — show all listings
    } else {
      query = query.or(userSchoolIds.length > 0 ? `school_id.in.(${userSchoolIds.join(',')}),visibility.eq.public` : `visibility.eq.public`);
      if (selectedSchool) query = query.eq('school_id', parseInt(selectedSchool));
    }

    if (filters.category) query = query.eq('category', filters.category);
    if (filters.condition) query = query.eq('condition', filters.condition);
    if (filters.maxPrice) query = query.lte('price', parseFloat(filters.maxPrice));
    if (filters.search) query = query.or(`title.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);

    if (filters.courseNumber) {
      const schoolIdsToSearch = selectedSchool ? [parseInt(selectedSchool)] : userSchoolIds;
      const { data: booklist } = await supabase
        .from('school_booklists')
        .select('isbn')
        .ilike('course_number', `%${filters.courseNumber}%`)
        .in('school_id', schoolIdsToSearch);

      if (!booklist || booklist.length === 0) {
        setCourseNoBooklist(true);
        setListings([]);
        setLoading(false);
        return;
      }

      const isbns = [...new Set(booklist.map(r => r.isbn))];
      const { data: textbookRows } = await supabase
        .from('textbook_details')
        .select('listing_id')
        .in('isbn', isbns);

      if (!textbookRows || textbookRows.length === 0) {
        setListings([]);
        setLoading(false);
        return;
      }

      query = query.in('id', textbookRows.map(r => r.listing_id)).eq('category', 'textbook');
    }

    const { data, error } = await query;

    if (!error) {
      let filtered = data || [];
      if (radius && userLat && userLng) {
        filtered = filtered.filter(listing => {
          if (!listing.lat || !listing.lng) return true;
          const dist = getDistance(userLat, userLng, listing.lat, listing.lng);
          return dist <= parseFloat(radius);
        });
      }
      setListings(filtered);
      if (filtered.length === 0 && filters.search) {
        fetchSuggestions(filters.search, filters.category);
      } else {
        setBookSuggestions([]);
      }
    }
    setLoading(false);
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleSearch = () => {
    fetchListings();
  };

  const conditionLabel = (condition) => {
    const labels = { new: 'New', like_new: 'Like New', good: 'Good', fair: 'Fair' };
    return labels[condition] || condition;
  };

  const categoryLabel = (category) => {
    const labels = { textbook: '📚 Textbook', clothing: '👕 Clothing', other: '🎒 Gear' };
    return labels[category] || category;
  };

  return (
    <div style={styles.pageWrapper}>
      <div style={styles.browseHeader}>
        <h1 style={styles.pageTitle}>Browse Listings</h1>
        <p style={styles.pageSubtitle}>Find textbooks, uniforms, and gear from families at your school.</p>
      </div>

      {!user ? (
        <div style={styles.emptyState}>
          <div style={{fontSize: '48px', marginBottom: '16px'}}>🏫</div>
          <h3 style={{color: '#555', marginBottom: '8px'}}>Sign in to browse listings</h3>
          <p style={{color: '#888', marginBottom: '20px'}}>Create a free account to see listings from families at your school.</p>
          <div style={{display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap'}}>
            <Link to="/signup" style={styles.primaryButton}>Create Account</Link>
            <Link to="/login" style={{...styles.primaryButton, backgroundColor: 'white', color: '#FF6B00', border: '2px solid #FF6B00'}}>Log In</Link>
          </div>
        </div>
) : (
        <>
          <div style={styles.searchBar}>
            <input
              style={styles.searchInput}
              type="text"
              name="search"
              placeholder="Search listings at your schools..."
              onChange={handleFilterChange}
              value={filters.search}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
            />
            <button style={styles.searchButton} onClick={handleSearch}>Search</button>
          </div>

        <div style={{...styles.browseLayout, flexDirection: isMobile ? 'column' : 'row'}}>
          <div style={{...styles.filterPanel, width: isMobile ? '100%' : '240px'}}>
            <h3 style={styles.filterTitle}>Filter Listings</h3>

            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Sort By</label>
              <select style={styles.filterInput} name="sort" onChange={handleFilterChange} value={filters.sort}>
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
              </select>
            </div>

<div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Show</label>
            <select
              style={styles.filterInput}
              name="status"
              onChange={handleFilterChange}
              value={filters.status}
            >
              <option value="available">Available Listings</option>
              <option value="sold">Sold & Pending (Price Research)</option>
            </select>
          </div>

<div style={styles.filterGroup}>
            <label style={styles.filterLabel}>School</label>
            <select
              style={styles.filterInput}
              value={selectedSchool}
              onChange={(e) => setSelectedSchool(e.target.value)}
            >
              <option value="">All My Schools</option>
              <option value="all">All Schools</option>
              {userSchools.map(school => (
                <option key={school.id} value={school.id}>{school.name}</option>
              ))}
            </select>
          </div>

            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Category</label>
              <select style={styles.filterInput} name="category" onChange={handleFilterChange} value={filters.category}>
                <option value="">All Categories</option>
                <option value="textbook">Textbooks</option>
                <option value="clothing">Uniforms & Clothing</option>
                <option value="other">Gear & Supplies</option>
              </select>
            </div>

            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Condition</label>
              <select style={styles.filterInput} name="condition" onChange={handleFilterChange} value={filters.condition}>
                <option value="">Any Condition</option>
                <option value="new">New</option>
                <option value="like_new">Like New</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
              </select>
            </div>

            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Max Price</label>
              <input style={styles.filterInput} type="number" name="maxPrice" placeholder="No limit" min={0} onChange={handleFilterChange} value={filters.maxPrice} />
            </div>

            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Distance</label>
              <select
                style={styles.filterInput}
                value={radius}
                onChange={(e) => setRadius(e.target.value)}
              >
                <option value="">Any Distance</option>
                <option value="5">Within 5 miles</option>
                <option value="10">Within 10 miles</option>
                <option value="25">Within 25 miles</option>
                <option value="50">Within 50 miles</option>
              </select>
              {radius && !userLat && (
                <p style={{fontSize: '11px', color: '#FF6B00', marginTop: '4px'}}>Add your zip code in Profile to use distance filter.</p>
              )}
            </div>

            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Course Number</label>
              <input style={styles.filterInput} type="text" name="courseNumber" placeholder="e.g. BIO101" onChange={handleFilterChange} value={filters.courseNumber} onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }} />
            </div>

            <button style={styles.filterButton} onClick={handleSearch}>Search</button>
          </div>

          {isMobile && <AdBanner isMobile={true} />}

          <div style={styles.listingsArea}>
            {loading && <p style={{color: '#888', textAlign: 'center', padding: '40px'}}>Loading listings...</p>}

{!loading && courseNoBooklist && (
  <div style={styles.emptyState}>
    <div style={{fontSize: '48px', marginBottom: '16px'}}>📋</div>
    <h3 style={{color: '#555', marginBottom: '8px'}}>No booklist found for this course</h3>
    <p style={{color: '#888'}}>Course number search is only available for schools that have provided their booklist. Let your school know they can send their booklist to <a href="mailto:hello@schoolyardsale.com" style={{color: '#FF6B00'}}>hello@schoolyardsale.com</a> to enable this feature.</p>
  </div>
)}

{!loading && !courseNoBooklist && listings.length === 0 && (
  <div style={styles.emptyState}>
    <div style={{fontSize: '48px', marginBottom: '16px'}}>🔍</div>
    <h3 style={{color: '#555', marginBottom: '8px'}}>No listings yet at your schools</h3>
    <p style={{color: '#888'}}>Be the first to post a listing in your school community!</p>
    <Link to="/post" style={{...styles.primaryButton, display: 'inline-block', marginTop: '20px'}}>Post a Listing</Link>

    {bookSuggestions.length > 0 && (
      <div style={{marginTop: '32px'}}>
        <h4 style={{color: '#555', marginBottom: '16px'}}>Find it online:</h4>
        {bookSuggestions[0].type === 'book' ? (
          <div style={{display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap'}}>
            {bookSuggestions.map((item, i) => (
              <div key={i} style={{background: '#fff', border: '1px solid #eee', borderRadius: '8px', padding: '16px', width: '160px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.08)'}}>
                {item.coverId
                  ? <img src={`https://covers.openlibrary.org/b/id/${item.coverId}-M.jpg`} alt={item.title} style={{width: '80px', marginBottom: '8px'}} />
                  : <div style={{fontSize: '40px', marginBottom: '8px'}}>📚</div>
                }
                <div style={{fontWeight: 'bold', fontSize: '13px', marginBottom: '4px'}}>{item.title}</div>
                <div style={{fontSize: '12px', color: '#888', marginBottom: '12px'}}>{item.author}</div>
                <a href={`https://www.amazon.com/s?k=${item.isbn || encodeURIComponent(item.title)}&tag=schoolyardsal-20`} target="_blank" rel="noopener noreferrer"
                  style={{display: 'block', background: '#FF9900', color: '#fff', borderRadius: '4px', padding: '6px', fontSize: '12px', textDecoration: 'none'}}>
                  Buy on Amazon
                </a>
              </div>
            ))}
          </div>
        ) : (
          <a href={`https://www.amazon.com/s?k=${encodeURIComponent(bookSuggestions[0].title)}&tag=schoolyardsal-20`} target="_blank" rel="noopener noreferrer"
            style={{display: 'inline-block', background: '#FF9900', color: '#fff', borderRadius: '6px', padding: '10px 20px', fontSize: '14px', fontWeight: 'bold', textDecoration: 'none'}}>
            Search Amazon
          </a>
        )}
      </div>
    )}
  </div>
)}


            {!loading && listings.length > 0 && (
              <div style={styles.listingsGrid}>
                {listings.map(listing => (
                  <div key={listing.id} style={styles.listingCard}>
                    {listing.image_url ? (
                      <img src={listing.image_url} alt={listing.title} style={styles.listingImage} />
                    ) : (
                      <div style={styles.listingImagePlaceholder}>
                        {listing.category === 'textbook' ? '📚' : listing.category === 'clothing' ? '👕' : '🎒'}
                      </div>
                    )}
                    <div style={styles.listingCardBody}>
                      <div style={styles.listingCategory}>{categoryLabel(listing.category)}</div>
                      <h3 style={styles.listingTitle}>{listing.title}</h3>
                      <p style={styles.listingSchool}>🏫 {listing.schools?.name}</p>
                      <div style={styles.listingMeta}>
                        <span style={styles.listingCondition}>{conditionLabel(listing.condition)}</span>
                        <span style={styles.listingPrice}>${parseFloat(listing.price).toFixed(2)}</span>
                      </div>
<div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <p style={{
                          fontSize: '11px', 
                          color: getListingAge(listing.created_at) === '🆕 New Listing!' ? '#FF6B00' : '#aaa', 
                          fontWeight: getListingAge(listing.created_at) === '🆕 New Listing!' ? 'bold' : 'normal',
                          marginBottom: '8px'
                        }}>
                          {getListingAge(listing.created_at) === '🆕 New Listing!' ? '🆕 New Listing!' : `Posted ${getListingAge(listing.created_at)}`}
                        </p>
                        <SaveButton listingId={listing.id} user={user} />
                      </div>
                      <Link to={`/listing/${listing.id}`} style={styles.viewButton}>
                        {filters.status === 'available' ? 'View Details' : 'View Price'}
                      </Link>
</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {!isMobile && <AdBanner isMobile={false} />}
        </div>
        </>
      )}
    </div>
  );
}

function SaveButton({ listingId, user }) {
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    const checkSaved = async () => {
      const { data } = await supabase
        .from('saved_listings')
        .select('id')
        .eq('user_id', user.id)
        .eq('listing_id', listingId)
        .maybeSingle();
      if (data) setSaved(true);
    };
    checkSaved();
  }, [listingId, user]);

  const toggleSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { alert('Please log in to save listings.'); return; }
    setLoading(true);
    if (saved) {
      await supabase.from('saved_listings').delete().eq('user_id', user.id).eq('listing_id', listingId);
      setSaved(false);
    } else {
      await supabase.from('saved_listings').insert({ user_id: user.id, listing_id: listingId });
      setSaved(true);
    }
    setLoading(false);
  };

  return (
    <button
      onClick={toggleSave}
      disabled={loading}
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontSize: '20px',
        padding: '4px',
        lineHeight: 1,
        opacity: loading ? 0.5 : 1
      }}
      title={saved ? 'Remove from saved' : 'Save listing'}
    >
      {saved ? '❤️' : '🤍'}
    </button>
  );
}

function SavedListings() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    supabase.from('saved_listings')
      .select('listing_id, listings(*, schools(name, city))')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        setListings(data?.map(d => d.listings) || []);
        setLoading(false);
      });
  }, [user, authLoading, navigate]);

  if (loading) return <div style={styles.pageWrapper}><p>Loading...</p></div>;

  const conditionLabel = (condition) => {
    const labels = {
      'new': 'New',
      'like_new': 'Like New',
      'good': 'Good',
      'fair': 'Fair',
      'poor': 'Poor'
    };
    return labels[condition] || condition;
  };

  return (
    <div style={styles.pageWrapper}>
      <h1 style={styles.pageTitle}>Saved Listings</h1>
      {listings.length === 0 ? (
        <p style={{color: '#888'}}>You haven't saved any listings yet. Browse listings and tap ❤️ to save them!</p>
      ) : (
        <div style={styles.listingsGrid}>
          {listings.map(listing => (
            <div key={listing.id} style={styles.listingCard} onClick={() => navigate(`/listing/${listing.id}`)}>
              {listing.image_url ? (
                <img src={listing.image_url} alt={listing.title} style={styles.listingImage} />
              ) : (
                <div style={styles.listingImagePlaceholder}>📦</div>
              )}
              <div style={styles.listingInfo}>
                <h3 style={styles.listingTitle}>{listing.title}</h3>
                <p style={{fontSize: '12px', color: '#888', marginBottom: '4px'}}>🏫 {listing.schools?.name}</p>
                <div style={styles.listingMeta}>
                  <span style={styles.listingCondition}>{conditionLabel(listing.condition)}</span>
                  <span style={styles.listingPrice}>${parseFloat(listing.price).toFixed(2)}</span>
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <p style={{
                    fontSize: '11px',
                    color: getListingAge(listing.created_at) === '🆕 New Listing!' ? '#FF6B00' : '#aaa',
                    fontWeight: getListingAge(listing.created_at) === '🆕 New Listing!' ? 'bold' : 'normal',
                    marginBottom: '8px'
                  }}>
                    {getListingAge(listing.created_at) === '🆕 New Listing!' ? '🆕 New Listing!' : `Posted ${getListingAge(listing.created_at)}`}
                  </p>
                  <SaveButton listingId={listing.id} user={user} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ListingDetail() {
  const [buyerName, setBuyerName] = useState('');
  const sendingRef = React.useRef(false);
  const [messageData, setMessageData] = useState({ name: '', message: '' });
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const [showContact, setShowContact] = useState(false);
  const [sellerEmail, setSellerEmail] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (user) {
      supabase.from('profiles').select('first_name, last_name').eq('id', user.id).single()
        .then(({ data }) => { if (data) setBuyerName(`${data.first_name} ${data.last_name}`); });
    }

    const fetchListing = async () => {
      const { data, error } = await supabase
        .from('listings')
        .select(`*, schools(name, city, zip_code)`)
        .eq('id', id)
        .single();

      if (!error && data) {
        setListing(data);

        if (data.category === 'textbook') {
          const { data: d } = await supabase.from('textbook_details').select('*').eq('listing_id', id).single();
          setDetails(d);
        } else if (data.category === 'clothing') {
          const { data: d } = await supabase.from('clothing_details').select('*').eq('listing_id', id).single();
          setDetails(d);
        } else if (data.category === 'other') {
          const { data: d } = await supabase.from('other_details').select('*').eq('listing_id', id).single();
          setDetails(d);
        }
      }
      setLoading(false);
    };

    fetchListing();
  }, [id]);

  const handleContactSeller = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('profiles')
      .select('email')
      .eq('id', listing.user_id)
      .single();
    if (data) {
      setSellerEmail(data.email);
      setShowContact(true);
    }
  };

const handleSendMessage = async () => {
    if (sendingRef.current || messageSent) return;
    if (!messageData.message) {
      alert('Please enter a message.');
      return;
    }
    sendingRef.current = true;
    setSendingMessage(true);

    try {
      const response = await fetch('/api/notify-seller', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sellerEmail: sellerEmail,
          listingTitle: listing.title,
          buyerEmail: user.email,
          buyerName: buyerName,
          message: messageData.message,
        }),
      });

      if (response.ok) {
        setMessageSent(true);
      } else {
        alert('Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message. Please try again.');
    }
    setSendingMessage(false);
        sendingRef.current = false;
  };

  const handleShare = async () => {
    const url = `https://www.schoolyardsale.com/listing/${listing.id}`;
    const shareData = {
      title: listing.title,
      text: `Check out this listing on SchoolYardSale: ${listing.title} for $${parseFloat(listing.price).toFixed(2)}`,
      url: url,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(url);
      alert('Link copied to clipboard!');
    }
  };

  const conditionLabel = (condition) => {
    const labels = { new: 'New', like_new: 'Like New', good: 'Good', fair: 'Fair' };
    return labels[condition] || condition;
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '80px', color: '#888' }}>Loading...</div>;
  if (!listing) return <div style={{ textAlign: 'center', padding: '80px', color: '#888' }}>Listing not found.</div>;

  return (
    <div style={styles.pageWrapper}>
      <div style={{...styles.detailLayout, flexDirection: isMobile ? 'column' : 'row'}}>
        <div style={{...styles.detailImageSection, flex: isMobile ? 'none' : '0 0 400px', width: isMobile ? '100%' : 'auto'}}>
          {listing.image_url ? (
            <img src={listing.image_url} alt={listing.title} style={styles.detailImage} />
          ) : (
            <div style={styles.detailImagePlaceholder}>
              {listing.category === 'textbook' ? '📚' : listing.category === 'clothing' ? '👕' : '🎒'}
            </div>
          )}
        </div>

        <div style={styles.detailInfoSection}>
          {listing.status !== 'available' && (
            <div style={{backgroundColor: '#FEE2E2', color: '#B91C1C', padding: '12px', borderRadius: '8px', marginBottom: '16px', fontWeight: 'bold'}}>
              {listing.status === 'sold' ? '❌ This item has been sold — viewing for price reference only.' : '⏳ This item is pending sale — viewing for price reference only.'}
            </div>
          )}
          <p style={styles.listingCategory}>
            {listing.category === 'textbook' ? '📚 Textbook' : listing.category === 'clothing' ? '👕 Clothing' : '🎒 Gear & Supplies'}
          </p>
          <h1 style={styles.detailTitle}>{listing.title}</h1>
          <p style={styles.detailPrice}>${parseFloat(listing.price).toFixed(2)}</p>
          <button onClick={handleShare} style={styles.shareButton}>
            📤 Share Listing
          </button>

          <div style={styles.detailTags}>
            <span style={styles.detailTag}>Condition: {conditionLabel(listing.condition)}</span>
            <span style={styles.detailTag}>🏫 {listing.schools?.name}</span>
            <span style={styles.detailTag}>📍 {listing.schools?.city}, MO</span>
            <span style={styles.detailTag}>🕒 Posted {getListingAge(listing.created_at)}</span>
          </div>

          {listing.description && (
            <div style={styles.detailSection}>
              <h3 style={styles.detailSectionTitle}>Description</h3>
              <p style={styles.detailSectionText}>{listing.description}</p>
            </div>
          )}

{details && listing.category === 'textbook' && (
            <div style={styles.detailSection}>
              <h3 style={styles.detailSectionTitle}>Book Details</h3>
              {details.author && <p style={styles.detailSectionText}>Author: {details.author}</p>}
              {details.edition && <p style={styles.detailSectionText}>Edition: {details.edition}</p>}
              {details.isbn && <p style={styles.detailSectionText}>ISBN: {details.isbn}</p>}
              {details.course && <p style={styles.detailSectionText}>Course: {details.course}</p>}
              {details.grade_level && <p style={styles.detailSectionText}>Grade Level: {details.grade_level}</p>}
              <div style={{marginTop: '12px', padding: '12px', backgroundColor: '#f9f9f9', borderRadius: '8px', border: '1px solid #eee'}}>
                <p style={{fontSize: '13px', color: '#666', marginBottom: '8px'}}>Can't find this locally? Buy online:</p>
                <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                  
                    <a href={`https://www.amazon.com/s?k=${encodeURIComponent(listing.title + (details.author ? ' ' + details.author : ''))}&tag=schoolyardsal-20`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.affiliateButton}
                  >
                    🛒 Buy on Amazon
                  </a>
                  
                    <a href={`https://www.chegg.com/search?q=${encodeURIComponent(listing.title)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{...styles.affiliateButton, backgroundColor: '#e87722'}}
                  >
                    📚 Rent on Chegg
                  </a>
                </div>
              </div>
            </div>
          )}

          {details && listing.category === 'clothing' && (
            <div style={styles.detailSection}>
              <h3 style={styles.detailSectionTitle}>Item Details</h3>
              {details.size && <p style={styles.detailSectionText}>Size: {details.size}</p>}
              {details.color && <p style={styles.detailSectionText}>Color: {details.color}</p>}
              {details.gender && <p style={styles.detailSectionText}>Gender: {details.gender}</p>}
              {details.grade_level && <p style={styles.detailSectionText}>Grade Level: {details.grade_level}</p>}
            </div>
          )}

          {details && listing.category === 'other' && (
            <div style={styles.detailSection}>
              <h3 style={styles.detailSectionTitle}>Item Details</h3>
              {details.brand && <p style={styles.detailSectionText}>Brand: {details.brand}</p>}
              {details.size && <p style={styles.detailSectionText}>Size: {details.size}</p>}
              {details.grade_level && <p style={styles.detailSectionText}>Grade Level: {details.grade_level}</p>}
            </div>
          )}

{listing.status === 'available' && !showContact && (
            <div>
              {user ? (
                <button style={styles.submitButton} onClick={handleContactSeller}>
                  Contact Seller
                </button>
              ) : (
                <div>
                  <p style={{ color: '#666', fontSize: '14px', marginBottom: '12px' }}>
                    You need an account to contact the seller.
                  </p>
                  <Link to="/signup" style={styles.primaryButton}>Create Account to Contact Seller</Link>
                </div>
              )}
            </div>
          )}

          {listing.status === 'available' && showContact && (
            <div style={styles.contactBox}>
              <h3 style={{ color: '#1a1a1a' }}>Contact Seller</h3>
              <p style={{ color: '#666', fontSize: '14px' }}>
                Reach out to arrange a meetup. Transactions happen in person.
              </p>
              {!messageSent ? (
                <>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Message</label>
                    <textarea
                      style={{ ...styles.formInput, height: '80px', resize: 'vertical' }}
                      placeholder="Hi, I'm interested in this item. Is it still available?"
                      value={messageData.message}
                      onChange={(e) => setMessageData({ ...messageData, message: e.target.value })}
                    />
                  </div>
                  <button
                    style={styles.submitButton}
                    onClick={handleSendMessage}
                    disabled={sendingMessage}
                  >
                    {sendingMessage ? 'Sending...' : '📧 Send Message to Seller'}
                  </button>
                </>
              ) : (
                <div style={{ backgroundColor: '#DCFCE7', color: '#16a34a', padding: '12px', borderRadius: '6px' }}>
                  ✅ Your message has been sent! The seller will reply to your email address.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MyListings() {
  const navigate = useNavigate();
  const { user, authLoading } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    fetchMyListings(user.id);
  }, [user, authLoading, navigate]);

  const fetchMyListings = async (userId) => {
    setLoading(true);
    const { data, error } = await supabase
      .from('listings')
      .select(`*, schools(name)`)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (!error) setListings(data || []);
    setLoading(false);
  };

  const handleStatusChange = async (listingId, newStatus) => {
    const { error } = await supabase
      .from('listings')
      .update({ status: newStatus })
      .eq('id', listingId);

    if (!error && user) {
      fetchMyListings(user.id);
    }
  };

  const handleDelete = async (listingId) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) return;
    const { error } = await supabase
      .from('listings')
      .delete()
      .eq('id', listingId);

    if (!error && user) {
      fetchMyListings(user.id);
    }
  };

  const statusColor = (status) => {
    const colors = { available: '#16a34a', pending: '#d97706', sold: '#6b7280', expired: '#dc2626' };
    return colors[status] || '#6b7280';
  };

  const statusLabel = (status) => {
    const labels = { available: 'Available', pending: 'Pending', sold: 'Sold', expired: 'Expired' };
    return labels[status] || status;
  };

  const handleRepost = async (listingId) => {
    const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
    const { error } = await supabase
      .from('listings')
      .update({ status: 'available', expires_at: expiresAt })
      .eq('id', listingId);
    if (!error && user) fetchMyListings(user.id);
  };

  const getExpiryInfo = (listing) => {
    if (!listing.expires_at || listing.status === 'sold') return null;
    const now = Date.now();
    const expiresAt = new Date(listing.expires_at);
    const msLeft = expiresAt - now;
    const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));
    if (listing.status === 'expired' || msLeft <= 0) {
      return { text: 'Expired', color: '#dc2626', urgent: true };
    }
    if (daysLeft <= 7) {
      return { text: `Expires in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`, color: '#d97706', urgent: true };
    }
    return { text: `Expires ${expiresAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`, color: '#888', urgent: false };
  };

  return (
    <div style={styles.pageWrapper}>
      <div style={styles.browseHeader}>
        <h1 style={styles.pageTitle}>My Listings</h1>
        <p style={styles.pageSubtitle}>Manage your active and past listings.</p>
      </div>

      <div style={{marginBottom: '24px'}}>
        <Link to="/post" style={styles.primaryButton}>+ Post a New Listing</Link>
      </div>

      {loading && <p style={{color: '#888', textAlign: 'center', padding: '40px'}}>Loading your listings...</p>}

      {!loading && listings.length === 0 && (
        <div style={styles.emptyState}>
          <div style={{fontSize: '48px', marginBottom: '16px'}}>📦</div>
          <h3 style={{color: '#555', marginBottom: '8px'}}>No listings yet</h3>
          <p style={{color: '#888'}}>Post your first listing to get started!</p>
          <Link to="/post" style={{...styles.primaryButton, display: 'inline-block', marginTop: '20px'}}>Post a Listing</Link>
        </div>
      )}

      {isMobile && <AdBanner isMobile={true} />}

      {!loading && listings.length > 0 && (
        <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
        <div style={styles.myListingsGrid}>
          {listings.map(listing => (
            <div key={listing.id} style={styles.myListingCard}>
              <div style={styles.myListingImageWrap}>
                {listing.image_url ? (
                  <img src={listing.image_url} alt={listing.title} style={styles.myListingImage} />
                ) : (
                  <div style={styles.myListingImagePlaceholder}>
                    {listing.category === 'textbook' ? '📚' : listing.category === 'clothing' ? '👕' : '🎒'}
                  </div>
                )}
              </div>

              <div style={styles.myListingInfo}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px'}}>
                  <h3 style={styles.myListingTitle}>{listing.title}</h3>
                  <span style={{...styles.statusBadge, backgroundColor: statusColor(listing.status)}}>
                    {statusLabel(listing.status)}
                  </span>
                </div>
                <p style={{fontSize: '13px', color: '#666', marginBottom: '4px'}}>🏫 {listing.schools?.name}</p>
                <p style={{fontSize: '18px', fontWeight: 'bold', color: '#FF6B00', marginBottom: '6px'}}>${parseFloat(listing.price).toFixed(2)}</p>
                {(() => { const expiry = getExpiryInfo(listing); return expiry ? (
                  <p style={{fontSize: '12px', color: expiry.color, fontWeight: expiry.urgent ? '600' : '400', marginBottom: '10px'}}>
                    {expiry.urgent ? '⚠ ' : ''}{expiry.text}
                  </p>
                ) : <div style={{marginBottom: '10px'}} />; })()}

                <div style={styles.myListingActions}>
                  {listing.status === 'available' && (
                    <button
                      style={styles.actionButtonYellow}
                      onClick={() => handleStatusChange(listing.id, 'pending')}
                    >
                      Mark Pending
                    </button>
                  )}
                  {listing.status === 'pending' && (
                    <>
                      <button
                        style={styles.actionButtonGreen}
                        onClick={() => handleStatusChange(listing.id, 'sold')}
                      >
                        Mark Sold
                      </button>
                      <button
                        style={styles.actionButtonGray}
                        onClick={() => handleStatusChange(listing.id, 'available')}
                      >
                        Relist
                      </button>
                    </>
                  )}
                  {listing.status === 'sold' && (
                    <button
                      style={styles.actionButtonGray}
                      onClick={() => handleStatusChange(listing.id, 'available')}
                    >
                      Relist
                    </button>
                  )}
                  {listing.status === 'expired' && (
                    <button
                      style={styles.actionButtonOrange}
                      onClick={() => handleRepost(listing.id)}
                    >
                      Repost
                    </button>
                  )}
                  {(listing.status === 'available' || listing.status === 'pending') && getExpiryInfo(listing)?.urgent && (
                    <button
                      style={styles.actionButtonOrange}
                      onClick={() => handleRepost(listing.id)}
                    >
                      Renew
                    </button>
                  )}
                 <Link to={`/listing/${listing.id}`} style={styles.actionButtonOrange}>View</Link>
<Link to={`/edit-listing/${listing.id}`} style={styles.actionButtonGray}>Edit</Link>
                  <button
                    style={styles.actionButtonRed}
                    onClick={() => handleDelete(listing.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
          </div>
          {!isMobile && <AdBanner isMobile={false} />}
        </div>
      )}
    </div>
  );
}

function EditListing() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    condition: '',
    category: '',
    isbn: '',
    author: '',
    edition: '',
    course: '',
    gradeLevel: '',
    itemType: '',
    gender: '',
    size: '',
    color: '',
    itemCategory: '',
    brand: '',
  });
  const [details, setDetails] = useState(null);
  const [visibility, setVisibility] = useState('school');

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }

    (async () => {
      const { data: listing, error } = await supabase
        .from('listings')
        .select('*')
        .eq('id', id)
        .single();

      if (error || !listing) {
        navigate('/my-listings');
        return;
      }

      if (listing.user_id !== user.id) {
        navigate('/my-listings');
        return;
      }

      setVisibility(listing.visibility || 'school');
      setFormData({
        title: listing.title || '',
        description: listing.description || '',
        price: listing.price || '',
        condition: listing.condition || '',
        category: listing.category || '',
        isbn: '', author: '', edition: '', course: '',
        gradeLevel: '', itemType: '', gender: '', size: '',
        color: '', itemCategory: '', brand: '',
      });

      if (listing.image_url) setImagePreview(listing.image_url);

      if (listing.category === 'textbook') {
        const { data: d } = await supabase.from('textbook_details').select('*').eq('listing_id', id).single();
        if (d) {
          setDetails(d);
          setFormData(prev => ({ ...prev, isbn: d.isbn || '', author: d.author || '', edition: d.edition || '', course: d.course || '', gradeLevel: d.grade_level || '' }));
        }
      } else if (listing.category === 'clothing') {
        const { data: d } = await supabase.from('clothing_details').select('*').eq('listing_id', id).single();
        if (d) {
          setDetails(d);
          setFormData(prev => ({ ...prev, itemType: d.item_type || '', gender: d.gender || '', size: d.size || '', color: d.color || '', gradeLevel: d.grade_level || '' }));
        }
      } else if (listing.category === 'other') {
        const { data: d } = await supabase.from('other_details').select('*').eq('listing_id', id).single();
        if (d) {
          setDetails(d);
          setFormData(prev => ({ ...prev, itemCategory: d.item_category || '', brand: d.brand || '', size: d.size || '', gradeLevel: d.grade_level || '' }));
        }
      }

      setLoading(false);
    })();
  }, [id, navigate, user, authLoading]);

const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const compressed = await compressImage(file, 800, 0.7);
      setImageFile(compressed);
      setImagePreview(URL.createObjectURL(compressed));
    }
  };

  const handleSave = async () => {
    setError('');
    if (!formData.title || !formData.price || !formData.condition) {
      setError('Please fill in all required fields.');
      return;
    }
    setSaving(true);

    let imageUrl = null;
    if (imageFile) {
      const fileExt = imageFile.name.split('.').pop();
      const fileName = `${user.id}-${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from('listing-images')
        .upload(fileName, imageFile);
      if (!uploadError) {
        const { data: urlData } = supabase.storage
          .from('listing-images')
          .getPublicUrl(fileName);
        imageUrl = urlData.publicUrl;
      }
    }

    const updateData = {
      title: formData.title,
      description: formData.description,
      price: Math.round(parseFloat(formData.price) * 100) / 100,
      condition: formData.condition,
      visibility,
    };
    if (imageUrl) updateData.image_url = imageUrl;

    await supabase.from('listings').update(updateData).eq('id', id);

    if (formData.category === 'textbook' && details) {
      await supabase.from('textbook_details').update({
        isbn: formData.isbn,
        author: formData.author,
        edition: formData.edition,
        course: formData.course,
        grade_level: formData.gradeLevel,
      }).eq('listing_id', id);
    } else if (formData.category === 'clothing' && details) {
      await supabase.from('clothing_details').update({
        item_type: formData.itemType,
        gender: formData.gender,
        size: formData.size,
        color: formData.color,
        grade_level: formData.gradeLevel,
      }).eq('listing_id', id);
    } else if (formData.category === 'other' && details) {
      await supabase.from('other_details').update({
        item_category: formData.itemCategory,
        brand: formData.brand,
        size: formData.size,
        grade_level: formData.gradeLevel,
      }).eq('listing_id', id);
    }

    setSaving(false);
    navigate('/my-listings');
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '80px', color: '#888' }}>Loading...</div>;

  return (
    <div style={styles.authWrapper}>
      <div style={{ ...styles.authCard, maxWidth: '600px' }}>
        <h1 style={styles.authTitle}>Edit Listing</h1>
        <p style={styles.authSubtitle}>Update your listing details</p>

        {error && <div style={styles.errorBox}>{error}</div>}

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Title *</label>
          <input style={styles.formInput} type="text" name="title" onChange={handleChange} value={formData.title} />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Price ($) *</label>
          <input style={styles.formInput} type="number" name="price" min="0" step="0.01" onChange={handleChange} value={formData.price} />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Condition *</label>
          <select style={styles.formInput} name="condition" onChange={handleChange} value={formData.condition}>
            <option value="">Select condition</option>
            <option value="new">New</option>
            <option value="like_new">Like New</option>
            <option value="good">Good</option>
            <option value="fair">Fair</option>
          </select>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Description</label>
          <textarea style={{ ...styles.formInput, height: '80px', resize: 'vertical' }} name="description" onChange={handleChange} value={formData.description} />
        </div>

        {formData.category === 'textbook' && (
          <>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Author</label>
              <input style={styles.formInput} type="text" name="author" onChange={handleChange} value={formData.author} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>ISBN</label>
              <input style={styles.formInput} type="text" name="isbn" onChange={handleChange} value={formData.isbn} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Edition</label>
              <input style={styles.formInput} type="text" name="edition" onChange={handleChange} value={formData.edition} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Course</label>
              <input style={styles.formInput} type="text" name="course" onChange={handleChange} value={formData.course} />
            </div>
          </>
        )}

        {formData.category === 'clothing' && (
          <>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Size</label>
              <input style={styles.formInput} type="text" name="size" onChange={handleChange} value={formData.size} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Color</label>
              <input style={styles.formInput} type="text" name="color" onChange={handleChange} value={formData.color} />
            </div>
          </>
        )}

        {formData.category === 'other' && (
          <>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Brand</label>
              <input style={styles.formInput} type="text" name="brand" onChange={handleChange} value={formData.brand} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Size</label>
              <input style={styles.formInput} type="text" name="size" onChange={handleChange} value={formData.size} />
            </div>
          </>
        )}

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Listing Visibility</label>
          <select style={styles.formInput} value={visibility} onChange={e => setVisibility(e.target.value)}>
            <option value="school">My School Only</option>
            <option value="public">Public (Anyone)</option>
          </select>
          <small style={{ color: '#888', display: 'block', marginTop: '4px' }}>
            {visibility === 'school' ? 'Only families at your school can see this listing.' : 'Anyone browsing SchoolYardSale can see this listing.'}
          </small>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Update Photo</label>
          <input type="file" accept="image/*" onChange={handleImageChange} style={styles.formInput} />
          {imagePreview && (
            <img src={imagePreview} alt="Preview" style={{ marginTop: '10px', width: '100%', maxHeight: '300px', objectFit: 'contain', borderRadius: '8px', background: '#f9f9f9' }} />
          )}
        </div>

        <div style={{display: 'flex', gap: '12px'}}>
          <button style={styles.submitButton} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            style={{...styles.submitButton, backgroundColor: '#6b7280'}}
            onClick={() => navigate('/my-listings')}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function Profile() {
  const [schoolSearch, setSchoolSearch] = useState('');
  const navigate = useNavigate();
  const { user, authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userSchools, setUserSchools] = useState([]);
  const [availableSchools, setAvailableSchools] = useState([]);
  const [selectedSchools, setSelectedSchools] = useState([]);
  const [zipCode, setZipCode] = useState('');
  const [city, setCity] = useState('');
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [emailReminders, setEmailReminders] = useState(true);
const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: '',
  });
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }

    (async () => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profile) {
        setFormData({
          firstName: profile.first_name || '',
          lastName: profile.last_name || '',
          email: profile.email || '',
          phone: profile.phone || '',
          role: profile.role || '',
        });
        setZipCode(profile.zip_code || '');
        setEmailReminders(profile.email_reminders !== false);
      }

      const { data: schools } = await supabase
        .from('user_schools')
        .select('school_id, schools(id, name, city)')
        .eq('user_id', user.id);

      if (schools) {
        setUserSchools(schools.map(s => s.schools));
        setSelectedSchools(schools.map(s => s.school_id));
      }

      setLoading(false);
    })();
  }, [navigate, user, authLoading]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handleSaveProfile = async () => {
    setError('');
    setSuccess('');
    setSaving(true);

let lat = null;
    let lng = null;
    if (zipCode && zipCode.length === 5) {
      const { data: zipData } = await supabase
        .from('zip_codes')
        .select('lat, lng')
        .eq('zip', zipCode)
        .single();
      if (zipData) { lat = zipData.lat; lng = zipData.lng; }
    }

    const { error } = await supabase
      .from('profiles')
      .update({
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        role: formData.role,
        zip_code: zipCode,
        lat,
        lng,
        email_reminders: emailReminders,
      })
      .eq('id', user.id);

    if (!error && formData.email !== user.email) {
      const { error: emailError } = await supabase.auth.updateUser({
        email: formData.email,
      });
      if (emailError) {
        setError('Profile saved but email update failed: ' + emailError.message);
        setSaving(false);
        return;
      }
      setSuccess('Profile updated! Check your new email for a confirmation link.');
    } else if (!error) {
      setSuccess('Profile updated successfully!');
    } else {
      setError(error.message);
    }
    setSaving(false);
  };

  const handleChangePassword = async () => {
    setError('');
    setSuccess('');
    if (!passwordData.newPassword || !passwordData.confirmPassword) {
      setError('Please fill in both password fields.');
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password: passwordData.newPassword });
    if (error) {
      setError(error.message);
    } else {
      setSuccess('Password updated successfully!');
      setPasswordData({ newPassword: '', confirmPassword: '' });
    }
    setSaving(false);
  };

  const handleZipSearch = async () => {
    if (zipCode.length !== 5) return;
    setLoadingSchools(true);
    const { data } = await supabase
      .from('schools')
      .select('*')
      .eq('zip_code', zipCode)
      .order('name');
    setAvailableSchools(data || []);
    setLoadingSchools(false);
  };

  const handleCitySearch = async () => {
    if (city.length < 3) return;
    setLoadingSchools(true);
    const { data } = await supabase
      .from('schools')
      .select('*')
      .ilike('city', `%${city}%`)
      .order('name');
    setAvailableSchools(data || []);
    setLoadingSchools(false);
  };

    const handleProfileSchoolSearch = async (e) => {
    const search = e.target.value;
    setSchoolSearch(search);
    if (search.length >= 3) {
      setLoadingSchools(true);
      const { data } = await supabase
        .from('schools')
        .select('*')
        .ilike('name', `%${search}%`)
        .order('name');
      setAvailableSchools(data || []);
      setLoadingSchools(false);
    } else {
      setAvailableSchools([]);
    }
  };

  const handleSchoolToggle = (schoolId) => {
    setSelectedSchools(prev =>
      prev.includes(schoolId)
        ? prev.filter(id => id !== schoolId)
        : [...prev, schoolId]
    );
  };

  const handleSaveSchools = async () => {
    setError('');
    setSuccess('');
    setSaving(true);

    await supabase
      .from('user_schools')
      .delete()
      .eq('user_id', user.id);

    if (selectedSchools.length > 0) {
      const inserts = selectedSchools.map(schoolId => ({
        user_id: user.id,
        school_id: schoolId,
      }));
      const { error } = await supabase.from('user_schools').insert(inserts);
      if (error) {
        setError(error.message);
        setSaving(false);
        return;
      }
    }

    const { data: schools } = await supabase
      .from('user_schools')
      .select('school_id, schools(id, name, city)')
      .eq('user_id', user.id);

    if (schools) {
      setUserSchools(schools.map(s => s.schools));
    }

    setSuccess('Schools updated successfully!');
    setSaving(false);
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '80px', color: '#888' }}>Loading...</div>;

  return (
    <div style={styles.pageWrapper}>
      <h1 style={styles.pageTitle}>My Profile</h1>

      {error && <div style={{...styles.errorBox, marginBottom: '24px'}}>{error}</div>}
      {success && <div style={{...styles.successBox, marginBottom: '24px'}}>{success}</div>}

      <div style={{...styles.profileGrid, gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))'}}>

        {/* Personal Info */}
        <div style={styles.profileCard}>
          <h2 style={styles.profileCardTitle}>Personal Information</h2>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>First Name</label>
            <input style={styles.formInput} type="text" name="firstName" onChange={handleChange} value={formData.firstName} />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Last Name</label>
            <input style={styles.formInput} type="text" name="lastName" onChange={handleChange} value={formData.lastName} />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Email</label>
            <input style={{...styles.formInput, backgroundColor: '#f5f5f5', color: '#888'}} type="email" name="email" value={formData.email} readOnly />
            <p style={{fontSize: '12px', color: '#888', marginTop: '4px'}}>To change your email contact us at hello@schoolyardsale.com</p>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Phone (optional)</label>
            <input style={styles.formInput} type="tel" name="phone" onChange={handleChange} value={formData.phone} />
          </div>

<div style={styles.formGroup}>
            <label style={styles.formLabel}>Zip Code</label>
            <input style={styles.formInput} type="text" name="zipCode" onChange={(e) => setZipCode(e.target.value)} value={zipCode} maxLength="5" />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>I am a...</label>
<select style={styles.formInput} name="role" onChange={handleChange} value={formData.role}>
            <option value="">Select role</option>
            <option value="parent">Parent of K-12 Student</option>
            <option value="k12student">K-12 Student</option>
            <option value="student">College Student</option>
            <option value="both">Parent and Student</option>
          </select>
          </div>

          <div style={styles.formGroup}>
            <label style={{display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#555', cursor: 'pointer'}}>
              <input
                type="checkbox"
                checked={emailReminders}
                onChange={(e) => setEmailReminders(e.target.checked)}
              />
              Send me listing expiry reminders
            </label>
          </div>

          <button style={styles.submitButton} onClick={handleSaveProfile} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {/* Change Password */}
        <div style={styles.profileCard}>
          <h2 style={styles.profileCardTitle}>Change Password</h2>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>New Password</label>
            <input style={styles.formInput} type="password" name="newPassword" placeholder="New password" onChange={handlePasswordChange} value={passwordData.newPassword} />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Confirm New Password</label>
            <input style={styles.formInput} type="password" name="confirmPassword" placeholder="Confirm new password" onChange={handlePasswordChange} value={passwordData.confirmPassword} />
          </div>

          <button style={styles.submitButton} onClick={handleChangePassword} disabled={saving}>
            {saving ? 'Saving...' : 'Update Password'}
          </button>
        </div>

        {/* Manage Schools */}
        <div style={{...styles.profileCard, gridColumn: '1 / -1'}}>
          <h2 style={styles.profileCardTitle}>My Schools</h2>

          <div style={{marginBottom: '16px'}}>
            <p style={{fontSize: '14px', color: '#555', marginBottom: '12px'}}>Your current schools:</p>
            {userSchools.length === 0 && <p style={{color: '#888', fontSize: '14px'}}>No schools associated yet.</p>}
<div style={{display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px'}}>
              {userSchools.map(school => (
                <span key={school.id} style={styles.schoolTag}>
                  🏫 {school.name}
                  <button
                    onClick={() => {
                      setSelectedSchools(prev => prev.filter(id => id !== school.id));
                      setUserSchools(prev => prev.filter(s => s.id !== school.id));
                    }}
                    style={styles.schoolTagX}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>

            {userSchools.length > 0 && (
              <button style={{...styles.submitButton, marginBottom: '16px'}} onClick={handleSaveSchools} disabled={saving}>
                {saving ? 'Saving...' : 'Save School Changes'}
              </button>
            )}
          </div>

          <p style={{fontSize: '14px', color: '#555', marginBottom: '12px'}}>Search to add or change schools:</p>

<div style={styles.formGroup}>
            <input
              style={styles.formInput}
              type="text"
              placeholder="Type your school name e.g. Vianney, Assumption..."
              value={schoolSearch}
              onChange={handleProfileSchoolSearch}
            />
            <p style={{fontSize: '12px', color: '#888', marginTop: '4px'}}>Type at least 3 letters to search</p>
          </div>

          {loadingSchools && <p style={{color: '#888', fontSize: '14px'}}>Loading schools...</p>}

          {availableSchools.length > 0 && (
            <div style={{marginBottom: '16px'}}>
              <p style={{fontSize: '13px', color: '#555', marginBottom: '8px'}}>Check all schools you are associated with:</p>
              <div style={styles.schoolList}>
                {availableSchools.map(school => (
                  <label key={school.id} style={styles.schoolOption}>
                    <input
                      type="checkbox"
                      checked={selectedSchools.includes(school.id)}
                      onChange={() => handleSchoolToggle(school.id)}
                      style={{marginRight: '8px'}}
                    />
                    {school.name} — {school.city}
                  </label>
                ))}
              </div>
            </div>
          )}

          {availableSchools.length > 0 && (
            <button style={styles.submitButton} onClick={handleSaveSchools} disabled={saving}>
              {saving ? 'Saving...' : 'Save Schools'}
            </button>
          )}
        </div>

      </div>
    </div>
  );
}

const compressImage = (file, maxWidth, quality) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (e) => {
      const img = new Image();
      img.src = e.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            resolve(new File([blob], file.name, { type: 'image/jpeg' }));
          },
          'image/jpeg',
          quality
        );
      };
    };
  });
};

function PostListing() {
  const [pricingData, setPricingData] = useState([]);
  const [isbnLoading, setIsbnLoading] = useState(false);
  const [isbnError, setIsbnError] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const navigate = useNavigate();
  const { user, authLoading } = useAuth();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [visibility, setVisibility] = useState('school');
  const [formData, setFormData] = useState({
    category: '',
    title: '',
    description: '',
    price: '',
    condition: '',
    schoolId: '',
    isbn: '',
    author: '',
    edition: '',
    course: '',
    gradeLevel: '',
    itemType: '',
    gender: '',
    size: '',
    color: '',
    itemCategory: '',
    brand: '',
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }

    supabase
      .from('user_schools')
      .select('school_id, schools(id, name)')
      .eq('user_id', user.id)
      .then(({ data: userSchools }) => {
        if (userSchools) {
          setSchools(userSchools.map(us => us.schools));
        }
      });
  }, [navigate, user, authLoading]);

const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (e.target.name === 'title' && e.target.value.length >= 3) {
      fetchPricingData(e.target.value, formData.category);
    }
  };

  const handleISBNLookup = async () => {
    if (!formData.isbn) {
      setIsbnError('Please enter an ISBN first.');
      return;
    }
    setIsbnLoading(true);
    setIsbnError('');
    try {
      const response = await fetch(`https://openlibrary.org/api/books?bibkeys=ISBN:${formData.isbn}&format=json&jscmd=data`);
      const data = await response.json();
      const key = `ISBN:${formData.isbn}`;
      if (data[key]) {
        const book = data[key];
        setFormData(prev => ({
          ...prev,
          title: book.title || prev.title,
          author: book.authors ? book.authors.map(a => a.name).join(', ') : prev.author,
          edition: book.edition_name || prev.edition,
        }));
        setIsbnError('');
      } else {
        setIsbnError('Book not found. Please enter details manually.');
      }
    } catch (error) {
      setIsbnError('Lookup failed. Please enter details manually.');
    }
    setIsbnLoading(false);
  };

const fetchPricingData = async (title, category) => {
    if (!title || title.length < 3 || !category) return;
    const { data, error } = await supabase
      .from('listings')
      .select('title, price, condition, status, schools(name)')
      .eq('category', category)
      .in('status', ['sold', 'pending'])
      .ilike('title', `%${title}%`)
      .order('created_at', { ascending: false })
      .limit(5);
    setPricingData(data || []);
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const compressed = await compressImage(file, 800, 0.7);
      setImageFile(compressed);
      setImagePreview(URL.createObjectURL(compressed));
    }
  };

  const handleSubmit = async () => {
    setError('');
if (!formData.category || !formData.price || !formData.condition || !formData.schoolId) {
      setError('Please fill in all required fields.');
      return;
    }
    if (!formData.title) {
      setError('Please enter a title or item name.');
      return;
    }
    setLoading(true);

let imageUrl = null;
if (imageFile) {
      const fileExt = imageFile.name.split('.').pop();
      const fileName = `${user.id}-${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from('listing-images')
        .upload(fileName, imageFile);
      if (uploadError) {
        console.error('Upload error:', uploadError);
        setError('Image upload failed: ' + uploadError.message);
        setLoading(false);
        return;
      } else {
        const { data: urlData } = supabase.storage
          .from('listing-images')
          .getPublicUrl(fileName);
        imageUrl = urlData.publicUrl;
        console.log('Image uploaded successfully:', imageUrl);
      }
    }

const { data: sellerProfile } = await supabase
      .from('profiles')
      .select('lat, lng')
      .eq('id', user.id)
      .single();

    const { data: listing, error: listingError } = await supabase
      .from('listings')
      .insert({
        user_id: user.id,
        school_id: parseInt(formData.schoolId),
        category: formData.category,
        title: formData.title,
        description: formData.description,
        price: Math.round(parseFloat(formData.price) * 100) / 100,
        condition: formData.condition,
        status: 'available',
        expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        image_url: imageUrl,
        lat: sellerProfile?.lat || null,
        lng: sellerProfile?.lng || null,
        visibility,
      })
      .select()
      .single();

    if (listingError) {
      setError(listingError.message);
      setLoading(false);
      return;
    }

    if (formData.category === 'textbook') {
      await supabase.from('textbook_details').insert({
        listing_id: listing.id,
        isbn: formData.isbn,
        author: formData.author,
        edition: formData.edition,
        course: formData.course,
        grade_level: formData.gradeLevel,
      });
    } else if (formData.category === 'clothing') {
      await supabase.from('clothing_details').insert({
        listing_id: listing.id,
        item_type: formData.itemType,
        gender: formData.gender,
        size: formData.size,
        color: formData.color,
        grade_level: formData.gradeLevel,
      });
    } else if (formData.category === 'other') {
      await supabase.from('other_details').insert({
        listing_id: listing.id,
        item_category: formData.itemCategory,
        brand: formData.brand,
        size: formData.size,
        grade_level: formData.gradeLevel,
      });
    }

    setLoading(false);
    navigate('/browse');
  };

  return (
    <div style={styles.authWrapper}>
      <div style={{ ...styles.authCard, maxWidth: '600px' }}>
        <h1 style={styles.authTitle}>Post a Listing</h1>
        <p style={styles.authSubtitle}>List something for families at your school</p>

        <div style={{background: '#fff8f0', border: '1px solid #fcd9b6', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px', fontSize: '13px', color: '#92400e'}}>
          Listings are active for <strong>30 days</strong>. You'll get an email in the last 7 days to renew if needed.
        </div>

        {error && <div style={styles.errorBox}>{error}</div>}

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>School *</label>
          <select style={styles.formInput} name="schoolId" onChange={handleChange} value={formData.schoolId}>
            <option value="">Select a school</option>
            {schools.map(school => (
              <option key={school.id} value={school.id}>{school.name}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
  <label>Listing Visibility</label>
  <select
    value={visibility}
    onChange={e => setVisibility(e.target.value)}
  >
    <option value="school">My School Only</option>
    <option value="public">Public (Anyone)</option>
  </select>
<small style={{ color: '#888', display: 'block', marginTop: '4px' }}>
  {visibility === 'school'
    ? 'Only families at your school can see this listing.'
    : 'Anyone browsing SchoolYardSale can see this listing.'}
</small>
</div>

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Category *</label>
          <select style={styles.formInput} name="category" onChange={handleChange} value={formData.category}>
            <option value="">Select a category</option>
            <option value="textbook">Textbook</option>
            <option value="clothing">Uniform / Clothing</option>
            <option value="other">Gear & Supplies</option>
          </select>
        </div>

        {formData.category === 'textbook' && (
          <>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>ISBN</label>
              <div style={{display: 'flex', gap: '8px'}}>
                <input style={{...styles.formInput, flex: 1}} type="text" name="isbn" placeholder="ISBN number" onChange={handleChange} value={formData.isbn} />
                <button style={{...styles.filterButton, width: 'auto', padding: '8px 16px'}} onClick={handleISBNLookup} type="button">
                  Look Up
                </button>
              </div>
              {isbnLoading && <p style={{fontSize: '12px', color: '#888', marginTop: '4px'}}>Looking up book...</p>}
              {isbnError && <p style={{fontSize: '12px', color: '#dc2626', marginTop: '4px'}}>{isbnError}</p>}
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Book Title *</label>
              <input style={styles.formInput} type="text" name="title" placeholder="e.g. AP Biology" onChange={handleChange} value={formData.title} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Author</label>
              <input style={styles.formInput} type="text" name="author" placeholder="Author name" onChange={handleChange} value={formData.author} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Edition</label>
              <input style={styles.formInput} type="text" name="edition" placeholder="e.g. 3rd Edition" onChange={handleChange} value={formData.edition} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Course / Subject</label>
              <input style={styles.formInput} type="text" name="course" placeholder="e.g. AP Biology, Algebra II" onChange={handleChange} value={formData.course} />
            </div>
          </>
        )}

        {formData.category === 'clothing' && (
          <>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Item Name *</label>
              <input style={styles.formInput} type="text" name="title" placeholder="e.g. Navy Uniform Polo" onChange={handleChange} value={formData.title} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Item Type</label>
              <select style={styles.formInput} name="itemType" onChange={handleChange} value={formData.itemType}>
                <option value="">Select type</option>
                <option value="uniform">Uniform piece</option>
                <option value="logo_apparel">School logo apparel</option>
                <option value="other">Other school clothing</option>
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Gender</label>
              <select style={styles.formInput} name="gender" onChange={handleChange} value={formData.gender}>
                <option value="">Select</option>
                <option value="boys">Boys</option>
                <option value="girls">Girls</option>
                <option value="unisex">Unisex</option>
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Size</label>
              <input style={styles.formInput} type="text" name="size" placeholder="e.g. Youth M, Adult L, Size 8" onChange={handleChange} value={formData.size} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Color</label>
              <input style={styles.formInput} type="text" name="color" placeholder="e.g. Navy Blue" onChange={handleChange} value={formData.color} />
            </div>
          </>
        )}

        {formData.category === 'other' && (
          <>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Item Name *</label>
              <input style={styles.formInput} type="text" name="title" placeholder="e.g. TI-84 Calculator" onChange={handleChange} value={formData.title} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Item Type</label>
              <select style={styles.formInput} name="itemCategory" onChange={handleChange} value={formData.itemCategory}>
                <option value="">Select type</option>
                <option value="calculator">Calculator</option>
                <option value="sports_equipment">Sports Equipment</option>
                <option value="musical_instrument">Musical Instrument</option>
                <option value="art_supplies">Art Supplies</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Brand</label>
              <input style={styles.formInput} type="text" name="brand" placeholder="Brand name" onChange={handleChange} value={formData.brand} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Size / Dimensions (optional)</label>
              <input style={styles.formInput} type="text" name="size" placeholder="e.g. 42 inch, Large" onChange={handleChange} value={formData.size} />
            </div>
          </>
        )}

        {formData.category && (
          <>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Grade Level</label>
              <select style={styles.formInput} name="gradeLevel" onChange={handleChange} value={formData.gradeLevel}>
                <option value="">Select grade level</option>
                <option value="elementary">Elementary</option>
                <option value="middle">Middle School</option>
                <option value="high">High School</option>
                <option value="college">College</option>
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Condition *</label>
              <select style={styles.formInput} name="condition" onChange={handleChange} value={formData.condition}>
                <option value="">Select condition</option>
                <option value="new">New</option>
                <option value="like_new">Like New</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Price ($) *</label>
              <input style={styles.formInput} type="number" name="price" placeholder="0.00" min="0" step="0.01" onChange={handleChange} value={formData.price} />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Description (optional)</label>
              <textarea style={{ ...styles.formInput, height: '80px', resize: 'vertical' }} name="description" placeholder="Any additional details about the item..." onChange={handleChange} value={formData.description} />
            </div>

            {pricingData.length > 0 && (
              <div style={styles.pricingBox}>
                <h4 style={{fontSize: '14px', fontWeight: 'bold', color: '#1a1a1a', marginBottom: '8px'}}>
                  💡 Similar items recently sold at your schools:
                </h4>
                {pricingData.map((item, index) => (
                  <div key={index} style={styles.pricingItem}>
                    <span style={{fontSize: '13px', color: '#555'}}>{item.title} — {item.schools?.name}</span>
                    <span style={{fontSize: '13px', fontWeight: 'bold', color: '#FF6B00'}}>${parseFloat(item.price).toFixed(2)} ({item.condition})</span>
                  </div>
                ))}
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Photo (optional)</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={styles.formInput}
              />
              {imagePreview && (
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{ marginTop: '10px', width: '100%', maxHeight: '300px', objectFit: 'contain', borderRadius: '8px', background: '#f9f9f9' }}
                />
              )}
            </div>
          </>
        )}

        <button style={styles.submitButton} onClick={handleSubmit} disabled={loading || !formData.category}>
          {loading ? 'Posting...' : 'Post Listing'}
        </button>
      </div>
    </div>
  );
}

function SignUp() {
  const [allSelectedSchools, setAllSelectedSchools] = useState([]);
  const navigate = useNavigate();
  const errorRef = React.useRef(null);
const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
    zipCode: '',
    city: '',
    schoolSearch: '',
    role: '',
  });
  const [schools, setSchools] = useState([]);
  const [selectedSchools, setSelectedSchools] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

const handleSchoolSearch = async (e) => {
    const search = e.target.value;
    setFormData({ ...formData, schoolSearch: search });
    if (search.length >= 3) {
      setLoadingSchools(true);
      const { data } = await supabase
        .from('schools')
        .select('*')
        .ilike('name', `%${search}%`)
        .order('name');
      setSchools(data || []);
      setLoadingSchools(false);
    } else {
      setSchools([]);
    }
  };

const handleSchoolToggle = (schoolId) => {
    const school = schools.find(s => s.id === schoolId);
    setSelectedSchools(prev => {
      if (prev.includes(schoolId)) {
        setAllSelectedSchools(prevSchools => prevSchools.filter(s => s.id !== schoolId));
        return prev.filter(id => id !== schoolId);
      } else {
        if (school) setAllSelectedSchools(prevSchools => [...prevSchools, school]);
        return [...prev, schoolId];
      }
    });
  };

  const handleGoogleSignUp = async () => {
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: 'https://www.schoolyardsale.com' },
    });
    if (oauthError) setError(oauthError.message);
  };

  const handleSignUp = async () => {
    setError('');
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password || !formData.role) {
      setError('Please fill in all required fields.');
      return;
    }
    const appleEmailDomains = ['icloud.com', 'me.com', 'mac.com'];
    const emailDomain = formData.email.split('@')[1]?.toLowerCase();
    if (appleEmailDomains.includes(emailDomain)) {
      setError('Apple email addresses (iCloud, me.com, mac.com) are not currently supported due to email delivery issues. Please sign up with a Gmail, Yahoo, or other email — or use "Continue with Google" above.');
      return;
    }
        if (!agreedToTerms) {
      setError('Please agree to the Terms of Service to continue.');
      return;
    }
    if (selectedSchools.length === 0) {
      setError('Please select at least one school.');
      return;
    }
    setLoading(true);

    const { data, error: signUpError } = await supabase.auth.signUp({
      email: formData.email,
      password: formData.password,
    });

if (signUpError) {
      if (signUpError.message.includes('already registered') || signUpError.message.includes('already been registered')) {
        setError('An account with this email already exists. Please log in instead.');
      } else {
        setError(signUpError.message);
      }
      setLoading(false);
      return;
    }

    let lat = null;
    let lng = null;
    if (formData.zipCode && formData.zipCode.length === 5) {
      const { data: zipData } = await supabase
        .from('zip_codes')
        .select('lat, lng')
        .eq('zip', formData.zipCode)
        .single();
      if (zipData) { lat = zipData.lat; lng = zipData.lng; }
    }

    const { error: profileError } = await supabase.from('profiles').insert({
      id: data.user.id,
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      zip_code: formData.zipCode,
      role: formData.role,
      lat,
      lng,
    });

    if (profileError) {
      setError(profileError.message);
      setLoading(false);
      return;
    }

    const schoolInserts = selectedSchools.map(schoolId => ({
      user_id: data.user.id,
      school_id: schoolId,
    }));

    const { error: schoolError } = await supabase
      .from('user_schools')
      .insert(schoolInserts);

    if (schoolError) {
      setError(schoolError.message);
      setLoading(false);
      return;
    }

setLoading(false);
    navigate('/confirm-email');
  };

  return (
    <div style={styles.authWrapper}>
      <div style={styles.authCard}>
        <h1 style={styles.authTitle}>Create your account</h1>
        <p style={styles.authSubtitle}>Join your school community marketplace</p>

        <button style={styles.googleButton} onClick={handleGoogleSignUp}>
          <GoogleIcon />
          Continue with Google
        </button>

        <div style={styles.orDividerRow}>
          <div style={styles.orDividerLine}></div>
          <span style={styles.orDividerText}>or sign up with email</span>
          <div style={styles.orDividerLine}></div>
        </div>

        {error && <div ref={errorRef} style={styles.errorBox}>{error}</div>}

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>First Name *</label>
          <input style={styles.formInput} type="text" name="firstName" placeholder="First name" onChange={handleChange} value={formData.firstName} />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Last Name *</label>
          <input style={styles.formInput} type="text" name="lastName" placeholder="Last name" onChange={handleChange} value={formData.lastName} />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Email *</label>
          <input style={styles.formInput} type="email" name="email" placeholder="your@email.com" onChange={handleChange} value={formData.email} />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Password *</label>
          <input style={styles.formInput} type="password" name="password" placeholder="Create a password" onChange={handleChange} value={formData.password} />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Phone (optional)</label>
          <input style={styles.formInput} type="tel" name="phone" placeholder="For future text alerts" onChange={handleChange} value={formData.phone} />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Zip Code (optional)</label>
          <input style={styles.formInput} type="text" name="zipCode" placeholder="e.g. 63122" onChange={handleChange} value={formData.zipCode} maxLength="5" />
        </div>

{selectedSchools.length > 0 && (
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Selected Schools</label>
            <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
              {selectedSchools.map(schoolId => {
                const school = schools.find(s => s.id === schoolId) || allSelectedSchools.find(s => s.id === schoolId);
                if (!school) return null;
                return (
                  <span key={schoolId} style={styles.schoolTag}>
                    🏫 {school.name}
                    <button
                      onClick={() => setSelectedSchools(prev => prev.filter(id => id !== schoolId))}
                      style={styles.schoolTagX}
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
          </div>
        )}

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Search for your school *</label>
          <input
            style={styles.formInput}
            type="text"
            name="schoolSearch"
            placeholder="Type your school name e.g. Vianney, Assumption..."
            onChange={handleSchoolSearch}
            value={formData.schoolSearch}
          />
          <p style={{fontSize: '12px', color: '#888', marginTop: '4px'}}>Type at least 3 letters to search</p>
        </div>

        {loadingSchools && <p style={{color: '#888', fontSize: '14px'}}>Searching...</p>}

        {schools.length > 0 && (
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Select Your School(s) *</label>
            <p style={{fontSize: '12px', color: '#888', marginBottom: '8px'}}>Check all schools you are associated with</p>
            <div style={styles.schoolList}>
              {schools.map(school => (
                <label key={school.id} style={styles.schoolOption}>
                  <input
                    type="checkbox"
                    checked={selectedSchools.includes(school.id)}
                    onChange={() => handleSchoolToggle(school.id)}
                    style={{marginRight: '8px'}}
                  />
                  {school.name} — {school.city}
                </label>
              ))}
            </div>
          </div>
        )}

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>I am a... *</label>
<select style={styles.formInput} name="role" onChange={handleChange} value={formData.role}>
            <option value="">Select role</option>
            <option value="parent">Parent of K-12 Student</option>
            <option value="k12student">K-12 Student</option>
            <option value="student">College Student</option>
            <option value="both">Parent and Student</option>
          </select>
        </div>

        <p style={styles.privacyNote}>
          🔒 Your contact info is only shared when you choose to reach out to a seller. We never sell your personal data.
        </p>

        <div style={{marginBottom: '16px'}}>
          <label style={{display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '13px', color: '#555', cursor: 'pointer'}}>
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              style={{marginTop: '2px', flexShrink: 0}}
            />
            <span>
              I agree to the <Link to="/terms" target="_blank" style={styles.authSwitchLink}>Terms of Service</Link> and <Link to="/privacy" target="_blank" style={styles.authSwitchLink}>Privacy Policy</Link>
            </span>
          </label>
        </div>

        <button style={styles.submitButton} onClick={handleSignUp} disabled={loading}>
          {loading ? 'Creating account...' : 'Create Account'}
        </button>


<p style={styles.authSwitch}>
          Already have an account? <Link to="/login" style={styles.authSwitchLink}>Log in</Link>
        </p>
      </div>
    </div>
  );
}

function Login() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const location = useLocation();
  const successMessage = location.state?.message;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogin = async () => {
    setError('');
    if (!formData.email || !formData.password) {
      setError('Please enter your email and password.');
      return;
    }
    setLoading(true);
    const { error: loginError } = await supabase.auth.signInWithPassword({
      email: formData.email,
      password: formData.password,
    });

    if (loginError) {
      setError(loginError.message);
      setLoading(false);
      return;
    }

    setLoading(false);
    navigate('/');
  };

  const handleGoogleSignIn = async () => {
    setError('');
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: 'https://www.schoolyardsale.com' },
    });
    if (oauthError) setError(oauthError.message);
  };

  return (
    <div style={styles.authWrapper}>
      <div style={styles.authCard}>
        <h1 style={styles.authTitle}>Welcome back</h1>
        <p style={styles.authSubtitle}>Log in to your SchoolYardSale account</p>

        {error && <div style={styles.errorBox}>{error}</div>}
        {successMessage && <div style={{...styles.successBox, marginBottom: '20px'}}>{successMessage}</div>}

        <button style={styles.googleButton} onClick={handleGoogleSignIn} disabled={loading}>
          <GoogleIcon />
          Continue with Google
        </button>

        <div style={styles.orDividerRow}>
          <div style={styles.orDividerLine}></div>
          <span style={styles.orDividerText}>or sign in with email</span>
          <div style={styles.orDividerLine}></div>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Email</label>
          <input style={styles.formInput} type="email" name="email" placeholder="your@email.com" onChange={handleChange} value={formData.email} />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Password</label>
          <input style={styles.formInput} type="password" name="password" placeholder="Your password" onChange={handleChange} value={formData.password} />
        </div>

        <button style={styles.submitButton} onClick={handleLogin} disabled={loading}>
          {loading ? 'Logging in...' : 'Log In'}
        </button>

        <p style={{textAlign: 'center', marginTop: '12px'}}>
          <Link to="/forgot-password" style={styles.authSwitchLink}>Forgot your password?</Link>
        </p>

        <p style={styles.authSwitch}>
          Don't have an account? <Link to="/signup" style={styles.authSwitchLink}>Sign up free</Link>
        </p>
      </div>
    </div>
  );
}

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleReset = async () => {
    setError('');
    if (!email) {
      setError('Please enter your email address.');
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: 'https://www.schoolyardsale.com/reset-password',
    });
    if (error) {
      setError(error.message);
    } else {
      setSent(true);
    }
    setLoading(false);
  };

  return (
    <div style={styles.authWrapper}>
      <div style={styles.authCard}>
        <h1 style={styles.authTitle}>Reset Password</h1>
        <p style={styles.authSubtitle}>Enter your email and we'll send you a reset link</p>

        {error && <div style={styles.errorBox}>{error}</div>}

        {!sent ? (
          <>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Email</label>
              <input
                style={styles.formInput}
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <button style={styles.submitButton} onClick={handleReset} disabled={loading}>
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
            <p style={styles.authSwitch}>
              Remember your password? <Link to="/login" style={styles.authSwitchLink}>Log in</Link>
            </p>
          </>
        ) : (
          <div>
            <div style={{...styles.successBox, marginBottom: '20px', padding: '16px'}}>
              ✅ Reset link sent! Check your email and click the link to reset your password.
            </div>
            <p style={{fontSize: '13px', color: '#888', textAlign: 'center'}}>
              Check your junk folder if you don't see it. The email comes from noreply@schoolyardsale.com
            </p>
            <p style={styles.authSwitch}>
              <Link to="/login" style={styles.authSwitchLink}>Back to Log In</Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleReset = async () => {
    setError('');
    if (!password || !confirmPassword) {
      setError('Please fill in both fields.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      setError(error.message);
    } else {
      navigate('/login', { state: { message: 'Password updated successfully! Please log in with your new password.' } });
    }
    setLoading(false);
  };

  return (
    <div style={styles.authWrapper}>
      <div style={styles.authCard}>
        <h1 style={styles.authTitle}>Set New Password</h1>
        <p style={styles.authSubtitle}>Enter your new password below</p>

        {error && <div style={styles.errorBox}>{error}</div>}

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>New Password</label>
          <input
            style={styles.formInput}
            type="password"
            placeholder="New password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Confirm New Password</label>
          <input
            style={styles.formInput}
            type="password"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </div>

        <button style={styles.submitButton} onClick={handleReset} disabled={loading}>
          {loading ? 'Updating...' : 'Update Password'}
        </button>
      </div>
    </div>
  );
}

function ConfirmEmail() {
  return (
    <div style={styles.authWrapper}>
      <div style={styles.authCard}>
        <div style={{textAlign: 'center', fontSize: '48px', marginBottom: '16px'}}>📧</div>
        <h1 style={styles.authTitle}>Check your email!</h1>
        <p style={{color: '#555', marginBottom: '16px', lineHeight: '1.6'}}>
          We sent a confirmation link to your email address. Click the link to activate your account.
        </p>
        <div style={{...styles.privacyNote, marginBottom: '20px'}}>
          <strong>Can't find it?</strong> Check your junk or spam folder. The email comes from noreply@schoolyardsale.com. To ensure you receive future emails, add noreply@schoolyardsale.com to your contacts.
        </div>
        <p style={{color: '#888', fontSize: '14px', textAlign: 'center'}}>
          Already confirmed? <Link to="/login" style={styles.authSwitchLink}>Log in here</Link>
        </p>
      </div>
    </div>
  );
}

function TermsOfService() {
  return (
    <div style={styles.pageWrapper}>
      <div style={{maxWidth: '800px', margin: '0 auto'}}>
        <h1 style={styles.pageTitle}>Terms of Service</h1>
        <p style={{color: '#888', marginBottom: '32px'}}>Last updated: March 2026</p>

        <div style={styles.legalSection}>
          <h2 style={styles.legalTitle}>1. About SchoolYardSale</h2>
          <p style={styles.legalText}>SchoolYardSale.com is an online marketplace that connects families within school communities to buy and sell used textbooks, uniforms, and school-related items. SchoolYardSale acts solely as a platform and is not a party to any transaction between buyers and sellers.</p>
        </div>

        <div style={styles.legalSection}>
          <h2 style={styles.legalTitle}>2. Acceptance of Terms</h2>
          <p style={styles.legalText}>By creating an account or using SchoolYardSale.com, you agree to these Terms of Service. If you do not agree, please do not use the site.</p>
        </div>

        <div style={styles.legalSection}>
          <h2 style={styles.legalTitle}>3. No Guarantee of Quality</h2>
          <p style={styles.legalText}>SchoolYardSale does not inspect, verify, or guarantee the quality, accuracy, safety, or legality of any item listed on the platform. All transactions are between buyers and sellers directly. We strongly encourage buyers to inspect items before completing any transaction.</p>
        </div>

        <div style={styles.legalSection}>
          <h2 style={styles.legalTitle}>4. Transactions</h2>
          <p style={styles.legalText}>All transactions are conducted offline between buyers and sellers. SchoolYardSale is not involved in and assumes no responsibility for any transaction, including but not limited to payment, delivery, condition of items, or disputes between parties.</p>
        </div>

        <div style={styles.legalSection}>
          <h2 style={styles.legalTitle}>5. User Responsibilities</h2>
          <p style={styles.legalText}>You agree to:</p>
          <ul style={{color: '#555', lineHeight: '1.8', paddingLeft: '24px'}}>
            <li>Provide accurate information in your listings</li>
            <li>Only list items that are legal to sell</li>
            <li>Not post fraudulent, misleading, or inappropriate listings</li>
            <li>Treat other users with respect</li>
            <li>Keep your account information secure</li>
          </ul>
        </div>

        <div style={styles.legalSection}>
          <h2 style={styles.legalTitle}>6. Prohibited Items</h2>
          <p style={styles.legalText}>The following items may not be listed on SchoolYardSale: illegal items, weapons, alcohol, tobacco, prescription medications, counterfeit goods, or any item prohibited by applicable law.</p>
        </div>

        <div style={styles.legalSection}>
          <h2 style={styles.legalTitle}>7. Content Removal</h2>
          <p style={styles.legalText}>SchoolYardSale reserves the right to remove any listing or suspend any account that violates these terms or that we determine, in our sole discretion, to be inappropriate.</p>
        </div>

        <div style={styles.legalSection}>
          <h2 style={styles.legalTitle}>8. Limitation of Liability</h2>
          <p style={styles.legalText}>SchoolYardSale shall not be liable for any damages arising from your use of the platform, including but not limited to disputes between buyers and sellers, item quality issues, or failed transactions. Use of this platform is at your own risk.</p>
        </div>

        <div style={styles.legalSection}>
          <h2 style={styles.legalTitle}>9. Changes to Terms</h2>
          <p style={styles.legalText}>We may update these terms at any time. Continued use of the site after changes constitutes acceptance of the new terms.</p>
        </div>

        <div style={styles.legalSection}>
          <h2 style={styles.legalTitle}>10. Contact</h2>
          <p style={styles.legalText}>Questions about these terms? Contact us at hello@schoolyardsale.com</p>
        </div>
      </div>
    </div>
  );
}

function PrivacyPolicy() {
  return (
    <div style={styles.pageWrapper}>
      <div style={{maxWidth: '800px', margin: '0 auto'}}>
        <h1 style={styles.pageTitle}>Privacy Policy</h1>
        <p style={{color: '#888', marginBottom: '32px'}}>Last updated: March 2026</p>

        <div style={styles.legalSection}>
          <h2 style={styles.legalTitle}>1. Information We Collect</h2>
          <p style={styles.legalText}>When you create an account we collect your name, email address, phone number (optional), zip code, and school associations. When you post a listing we collect the listing details and any photos you upload.</p>
        </div>

        <div style={styles.legalSection}>
          <h2 style={styles.legalTitle}>2. How We Use Your Information</h2>
          <p style={styles.legalText}>We use your information to:</p>
          <ul style={{color: '#555', lineHeight: '1.8', paddingLeft: '24px'}}>
            <li>Operate the SchoolYardSale marketplace</li>
            <li>Show your listings to buyers at your school</li>
            <li>Send you notifications about your listings</li>
            <li>Improve the platform</li>
            <li>Show relevant advertisements to users</li>
          </ul>
        </div>

        <div style={styles.legalSection}>
          <h2 style={styles.legalTitle}>3. Information Sharing</h2>
          <p style={styles.legalText}>Your contact information is only shared with other users when you choose to respond to a buyer inquiry. We do not sell your personal information to third parties.</p>
        </div>

        <div style={styles.legalSection}>
          <h2 style={styles.legalTitle}>4. Advertising</h2>
          <p style={styles.legalText}>SchoolYardSale may display advertisements from local businesses and schools. Advertisers do not have access to your personal information. We may use general information such as your school association and grade level to show relevant ads.</p>
        </div>

        <div style={styles.legalSection}>
          <h2 style={styles.legalTitle}>5. Data Security</h2>
          <p style={styles.legalText}>We use industry-standard security measures to protect your data. Your password is encrypted and never stored in plain text. However no system is 100% secure and we cannot guarantee absolute security.</p>
        </div>

        <div style={styles.legalSection}>
          <h2 style={styles.legalTitle}>6. Your Rights</h2>
          <p style={styles.legalText}>You may update or delete your account information at any time by visiting your profile page. To request complete deletion of your data contact us at hello@schoolyardsale.com.</p>
        </div>

        <div style={styles.legalSection}>
          <h2 style={styles.legalTitle}>7. Cookies</h2>
          <p style={styles.legalText}>SchoolYardSale uses cookies and similar technologies to keep you logged in and improve your experience. By using the site you consent to our use of cookies.</p>
        </div>

        <div style={styles.legalSection}>
          <h2 style={styles.legalTitle}>8. Contact</h2>
          <p style={styles.legalText}>Questions about this privacy policy? Contact us at hello@schoolyardsale.com</p>
        </div>
      </div>
    </div>
  );
}

const styles = {
  nav: { backgroundColor: '#FF6B00', padding: '0 20px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
  navInner: { maxWidth: '1100px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '64px' },
  logo: { color: 'white', fontSize: '22px', fontWeight: 'bold', textDecoration: 'none' },
  navLinks: { display: 'flex', alignItems: 'center', gap: '24px' },
  navLink: { color: 'white', textDecoration: 'none', fontSize: '15px' },
  navLinkButton: { backgroundColor: 'white', color: '#FF6B00', padding: '8px 16px', borderRadius: '6px', textDecoration: 'none', fontWeight: 'bold', fontSize: '15px' },
  navLinkOutline: { backgroundColor: 'transparent', color: 'white', border: '2px solid white', padding: '6px 14px', borderRadius: '6px', fontWeight: 'bold', fontSize: '15px', cursor: 'pointer' },
  hero: { backgroundColor: '#FFF7F0', padding: '80px 20px', textAlign: 'center' },
  heroTitle: { fontSize: '42px', fontWeight: 'bold', color: '#1a1a1a', marginBottom: '16px' },
  heroSubtitle: { fontSize: '18px', color: '#555', maxWidth: '600px', margin: '0 auto 32px', lineHeight: '1.6' },
  heroButtons: { display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' },
  primaryButton: { backgroundColor: '#FF6B00', color: 'white', padding: '14px 28px', borderRadius: '8px', textDecoration: 'none', fontWeight: 'bold', fontSize: '16px' },
  secondaryButton: { backgroundColor: 'white', color: '#FF6B00', padding: '14px 28px', borderRadius: '8px', textDecoration: 'none', fontWeight: 'bold', fontSize: '16px', border: '2px solid #FF6B00' },
  categories: { padding: '60px 20px', maxWidth: '1100px', margin: '0 auto', textAlign: 'center' },
  sectionTitle: { fontSize: '28px', fontWeight: 'bold', marginBottom: '40px', color: '#1a1a1a' },
  categoryGrid: { display: 'flex', gap: '24px', justifyContent: 'center', flexWrap: 'wrap' },
  categoryCard: { backgroundColor: 'white', border: '1px solid #eee', borderRadius: '12px', padding: '32px 24px', width: '260px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  categoryIcon: { fontSize: '40px', marginBottom: '16px' },
  howItWorks: { backgroundColor: '#FFF7F0', padding: '60px 20px', textAlign: 'center' },
  stepsGrid: { display: 'flex', gap: '32px', justifyContent: 'center', flexWrap: 'wrap', maxWidth: '900px', margin: '0 auto' },
  step: { width: '240px' },
  stepNumber: { backgroundColor: '#FF6B00', color: 'white', width: '40px', height: '40px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '18px', margin: '0 auto 16px' },
  pageWrapper: { maxWidth: '1100px', margin: '0 auto', padding: '40px 20px' },
  browseHeader: { marginBottom: '32px' },
  pageTitle: { fontSize: '32px', fontWeight: 'bold', color: '#1a1a1a', marginBottom: '8px' },
  pageSubtitle: { color: '#666', fontSize: '16px' },
  browseLayout: { display: 'flex', gap: '32px', alignItems: 'flex-start' },
  filterPanel: { backgroundColor: 'white', border: '1px solid #eee', borderRadius: '12px', padding: '24px', width: '240px', flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  filterTitle: { fontSize: '16px', fontWeight: 'bold', marginBottom: '20px', color: '#1a1a1a' },
  filterGroup: { marginBottom: '16px' },
  filterLabel: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '6px' },
  filterInput: { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' },
  filterButton: { width: '100%', backgroundColor: '#FF6B00', color: 'white', border: 'none', padding: '10px', borderRadius: '6px', fontWeight: 'bold', fontSize: '15px', cursor: 'pointer', marginTop: '8px' },
  listingsArea: { flex: 1 },
  emptyState: { textAlign: 'center', padding: '80px 20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #eee' },
  listingsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '20px' },
  listingCard: { backgroundColor: 'white', border: '1px solid #eee', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  listingImage: { width: '100%', height: '180px', objectFit: 'contain', background: '#f9f9f9' },
  listingImagePlaceholder: { width: '100%', height: '180px', backgroundColor: '#FFF7F0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '48px' },
  detailImage: { width: '100%', borderRadius: '12px', objectFit: 'cover', maxHeight: '350px' },
  listingCardBody: { padding: '16px' },
  listingCategory: { fontSize: '12px', color: '#FF6B00', fontWeight: '600', marginBottom: '4px' },
  listingTitle: { fontSize: '16px', fontWeight: 'bold', color: '#1a1a1a', marginBottom: '6px' },
  listingSchool: { fontSize: '13px', color: '#666', marginBottom: '10px' },
  listingMeta: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
  listingCondition: { fontSize: '12px', backgroundColor: '#f0f0f0', padding: '3px 8px', borderRadius: '4px', color: '#555' },
  listingPrice: { fontSize: '18px', fontWeight: 'bold', color: '#FF6B00' },
  viewButton: { display: 'block', textAlign: 'center', backgroundColor: '#FF6B00', color: 'white', padding: '8px', borderRadius: '6px', textDecoration: 'none', fontWeight: 'bold', fontSize: '14px' },
  detailLayout: { display: 'flex', gap: '40px', alignItems: 'flex-start', flexWrap: 'wrap' },
  detailImageSection: { flex: '0 0 400px' },
  detailImage: { width: '100%', borderRadius: '12px', objectFit: 'contain', maxHeight: '400px', background: '#f9f9f9' },
  detailImagePlaceholder: { width: '100%', height: '300px', backgroundColor: '#FFF7F0', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '80px' },
  detailInfoSection: { flex: 1, minWidth: '280px' },
  detailTitle: { fontSize: '28px', fontWeight: 'bold', color: '#1a1a1a', marginBottom: '8px' },
  detailPrice: { fontSize: '32px', fontWeight: 'bold', color: '#FF6B00', marginBottom: '16px' },
  detailTags: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '24px' },
  detailTag: { backgroundColor: '#f5f5f5', padding: '6px 12px', borderRadius: '20px', fontSize: '13px', color: '#555' },
  detailSection: { marginBottom: '20px' },
  detailSectionTitle: { fontSize: '16px', fontWeight: 'bold', color: '#1a1a1a', marginBottom: '8px' },
  detailSectionText: { fontSize: '14px', color: '#555', marginBottom: '4px' },
    contactBox: { backgroundColor: '#FFF7F0', border: '1px solid #FFD4A8', borderRadius: '12px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' },
  authWrapper: { display: 'flex', justifyContent: 'center', padding: '60px 20px' },
  authCard: { backgroundColor: 'white', border: '1px solid #eee', borderRadius: '12px', padding: '40px', width: '100%', maxWidth: '480px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' },
  authTitle: { fontSize: '26px', fontWeight: 'bold', color: '#1a1a1a', marginBottom: '8px' },
  authSubtitle: { color: '#666', marginBottom: '28px', fontSize: '15px' },
  formGroup: { marginBottom: '18px' },
  formLabel: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '6px' },
  formInput: { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '15px', boxSizing: 'border-box' },
  privacyNote: { fontSize: '13px', color: '#666', backgroundColor: '#FFF7F0', padding: '12px', borderRadius: '6px', marginBottom: '20px', lineHeight: '1.5' },
  submitButton: { width: '100%', backgroundColor: '#FF6B00', color: 'white', border: 'none', padding: '12px', borderRadius: '8px', fontWeight: 'bold', fontSize: '16px', cursor: 'pointer' },
  authSwitch: { textAlign: 'center', marginTop: '20px', color: '#666', fontSize: '14px' },
  authSwitchLink: { color: '#FF6B00', fontWeight: 'bold', textDecoration: 'none' },
  errorBox: { backgroundColor: '#FEE2E2', color: '#B91C1C', padding: '12px', borderRadius: '6px', marginBottom: '20px', fontSize: '14px' },
  googleButton: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', width: '100%', padding: '11px', backgroundColor: '#fff', border: '1px solid #dadce0', borderRadius: '6px', fontSize: '15px', fontWeight: '500', color: '#3c4043', cursor: 'pointer', marginBottom: '16px', boxSizing: 'border-box' },
  orDividerRow: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' },
  orDividerLine: { flex: 1, height: '1px', backgroundColor: '#e5e7eb' },
  orDividerText: { fontSize: '13px', color: '#9ca3af', whiteSpace: 'nowrap' },
  schoolList: { border: '1px solid #ddd', borderRadius: '6px', padding: '8px', maxHeight: '200px', overflowY: 'auto' },
  schoolOption: { display: 'flex', alignItems: 'center', padding: '8px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' },
  orDivider: { display: 'flex', alignItems: 'center', margin: '4px 0', color: '#aaa' },
  orText: { fontSize: '12px', color: '#aaa', margin: '0 auto' },
  myListingsGrid: { display: 'flex', flexDirection: 'column', gap: '16px' },
  myListingCard: { backgroundColor: 'white', border: '1px solid #eee', borderRadius: '12px', padding: '16px', display: 'flex', gap: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  myListingImageWrap: { flexShrink: 0 },
  myListingImage: { width: '100px', height: '100px', objectFit: 'contain', borderRadius: '8px', background: '#f9f9f9' },
  myListingImagePlaceholder: { width: '100px', height: '100px', backgroundColor: '#FFF7F0', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '36px' },
  myListingInfo: { flex: 1 },
  myListingTitle: { fontSize: '16px', fontWeight: 'bold', color: '#1a1a1a', margin: 0 },
  statusBadge: { fontSize: '11px', color: 'white', padding: '3px 8px', borderRadius: '20px', fontWeight: 'bold', flexShrink: 0 },
  myListingActions: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  actionButtonYellow: { backgroundColor: '#d97706', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: 'bold' },
  actionButtonGreen: { backgroundColor: '#16a34a', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: 'bold' },
  actionButtonGray: { backgroundColor: '#6b7280', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: 'bold' },
  actionButtonOrange: { backgroundColor: '#FF6B00', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: 'bold', textDecoration: 'none' },
  actionButtonRed: { backgroundColor: '#dc2626', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: 'bold' },
profileGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '24px' },
  profileCard: { backgroundColor: 'white', border: '1px solid #eee', borderRadius: '12px', padding: '28px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  profileCardTitle: { fontSize: '18px', fontWeight: 'bold', color: '#1a1a1a', marginBottom: '20px' },
  successBox: { backgroundColor: '#DCFCE7', color: '#16a34a', padding: '12px', borderRadius: '6px', fontSize: '14px' },
  schoolTag: { backgroundColor: '#FFF7F0', border: '1px solid #FFD4A8', color: '#FF6B00', padding: '6px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '600' },
schoolTagX: { background: 'none', border: 'none', color: '#FF6B00', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', marginLeft: '6px', padding: '0', lineHeight: 1 },
searchBar: { display: 'flex', gap: '12px', marginBottom: '24px' },
  searchInput: { flex: 1, padding: '12px 16px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box' },
  searchButton: { backgroundColor: '#FF6B00', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', fontWeight: 'bold', fontSize: '15px', cursor: 'pointer' },
hamburger: { display: 'none', flexDirection: 'column', gap: '5px', cursor: 'pointer', background: 'none', border: 'none', padding: '4px' },
  hamburgerLine: { width: '24px', height: '2px', backgroundColor: 'white' },
  mobileMenu: { display: 'none', flexDirection: 'column', backgroundColor: '#FF6B00', padding: '16px 20px', gap: '16px' },
  mobileMenuLink: { color: 'white', textDecoration: 'none', fontSize: '16px', fontWeight: '500' },
  pricingBox: { backgroundColor: '#FFF7F0', border: '1px solid #FFD4A8', borderRadius: '8px', padding: '16px', marginBottom: '16px' },
  pricingItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #FFE8CC' },
  affiliateButton: { backgroundColor: '#FF9900', color: 'white', padding: '8px 16px', borderRadius: '6px', textDecoration: 'none', fontWeight: 'bold', fontSize: '13px' },
    shareButton: { backgroundColor: 'white', color: '#FF6B00', border: '2px solid #FF6B00', padding: '8px 16px', borderRadius: '6px', fontWeight: 'bold', fontSize: '14px', cursor: 'pointer', marginBottom: '16px' },
    legalSection: { marginBottom: '32px' },
  legalTitle: { fontSize: '18px', fontWeight: 'bold', color: '#1a1a1a', marginBottom: '12px' },
  legalText: { color: '#555', lineHeight: '1.8', fontSize: '15px' },
    footer: { backgroundColor: '#f5f5f5', borderTop: '1px solid #eee', padding: '20px', textAlign: 'center', fontSize: '13px', color: '#888', marginTop: '40px' },
  footerLink: { color: '#FF6B00', textDecoration: 'none' },

};

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

function AuthStateHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    if (!user || location.pathname === '/complete-profile') return;
    supabase.from('profiles').select('id').eq('id', user.id).single()
      .then(({ data: profile }) => {
        if (!profile) navigate('/complete-profile');
      });
  }, [user, navigate, location.pathname]);

  return null;
}

function CompleteProfile() {
  const navigate = useNavigate();
  const { user, authLoading } = useAuth();
  const [formData, setFormData] = useState({ firstName: '', lastName: '', phone: '', zipCode: '', role: '' });
  const [schools, setSchools] = useState([]);
  const [selectedSchools, setSelectedSchools] = useState([]);
  const [allSelectedSchools, setAllSelectedSchools] = useState([]);
  const [schoolSearch, setSchoolSearch] = useState('');
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const errorRef = React.useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    const meta = user.user_metadata || {};
    setFormData(f => ({
      ...f,
      firstName: meta.given_name || meta.full_name?.split(' ')[0] || '',
      lastName: meta.family_name || meta.full_name?.split(' ').slice(1).join(' ') || '',
    }));
  }, [navigate, user, authLoading]);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSchoolSearch = async (e) => {
    const search = e.target.value;
    setSchoolSearch(search);
    if (search.length >= 3) {
      setLoadingSchools(true);
      const { data } = await supabase.from('schools').select('*').ilike('name', `%${search}%`).order('name');
      setSchools(data || []);
      setLoadingSchools(false);
    } else {
      setSchools([]);
    }
  };

  const handleSchoolToggle = (schoolId) => {
    const school = schools.find(s => s.id === schoolId);
    setSelectedSchools(prev => {
      if (prev.includes(schoolId)) {
        setAllSelectedSchools(prevSchools => prevSchools.filter(s => s.id !== schoolId));
        return prev.filter(id => id !== schoolId);
      } else {
        if (school) setAllSelectedSchools(prevSchools => [...prevSchools, school]);
        return [...prev, schoolId];
      }
    });
  };

  const handleSubmit = async () => {
    setError('');
    if (!formData.firstName || !formData.lastName || !formData.role) {
      setError('Please fill in your name and role.');
      return;
    }
    if (selectedSchools.length === 0) {
      setError('Please select at least one school.');
      return;
    }
    setLoading(true);

    let lat = null, lng = null;
    if (formData.zipCode && formData.zipCode.length === 5) {
      const { data: zipData } = await supabase.from('zip_codes').select('lat, lng').eq('zip', formData.zipCode).single();
      if (zipData) { lat = zipData.lat; lng = zipData.lng; }
    }

    const { error: profileError } = await supabase.from('profiles').insert({
      id: user.id,
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: user.email,
      phone: formData.phone,
      zip_code: formData.zipCode,
      role: formData.role,
      lat,
      lng,
    });

    if (profileError) { setError(profileError.message); setLoading(false); return; }

    const { error: schoolError } = await supabase.from('user_schools').insert(
      selectedSchools.map(schoolId => ({ user_id: user.id, school_id: schoolId }))
    );

    if (schoolError) { setError(schoolError.message); setLoading(false); return; }

    setLoading(false);
    navigate('/');
  };

  return (
    <div style={styles.authWrapper}>
      <div style={styles.authCard}>
        <h1 style={styles.authTitle}>Almost there!</h1>
        <p style={styles.authSubtitle}>Just a couple more details to set up your account</p>

        {error && <div ref={errorRef} style={styles.errorBox}>{error}</div>}

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>First Name *</label>
          <input style={styles.formInput} type="text" name="firstName" placeholder="First name" onChange={handleChange} value={formData.firstName} />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Last Name *</label>
          <input style={styles.formInput} type="text" name="lastName" placeholder="Last name" onChange={handleChange} value={formData.lastName} />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Phone (optional)</label>
          <input style={styles.formInput} type="tel" name="phone" placeholder="For future text alerts" onChange={handleChange} value={formData.phone} />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Zip Code (optional)</label>
          <input style={styles.formInput} type="text" name="zipCode" placeholder="e.g. 63122" onChange={handleChange} value={formData.zipCode} maxLength="5" />
        </div>

        {selectedSchools.length > 0 && (
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Selected Schools</label>
            <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
              {selectedSchools.map(schoolId => {
                const school = allSelectedSchools.find(s => s.id === schoolId);
                if (!school) return null;
                return (
                  <span key={schoolId} style={styles.schoolTag}>
                    🏫 {school.name}
                    <button onClick={() => { setSelectedSchools(prev => prev.filter(id => id !== schoolId)); setAllSelectedSchools(prev => prev.filter(s => s.id !== schoolId)); }} style={styles.schoolTagX}>×</button>
                  </span>
                );
              })}
            </div>
          </div>
        )}

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Search for your school *</label>
          <input style={styles.formInput} type="text" placeholder="Type your school name e.g. Vianney, Assumption..." value={schoolSearch} onChange={handleSchoolSearch} />
          <p style={{fontSize: '12px', color: '#888', marginTop: '4px'}}>Type at least 3 letters to search</p>
          {loadingSchools && <p style={{fontSize: '13px', color: '#888'}}>Searching...</p>}
          {schools.length > 0 && (
            <div style={styles.schoolList}>
              {schools.map(school => (
                <div key={school.id} style={styles.schoolOption} onClick={() => handleSchoolToggle(school.id)}>
                  <input type="checkbox" checked={selectedSchools.includes(school.id)} onChange={() => {}} style={{marginRight: '8px'}} />
                  {school.name} {school.city ? `— ${school.city}` : ''}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>I am a... *</label>
          <select style={styles.formInput} name="role" onChange={handleChange} value={formData.role}>
            <option value="">Select role</option>
            <option value="parent">Parent</option>
            <option value="student">Student</option>
            <option value="alumni">Alumni</option>
            <option value="teacher">Teacher / Staff</option>
          </select>
        </div>

        <button style={styles.submitButton} onClick={handleSubmit} disabled={loading}>
          {loading ? 'Saving...' : 'Complete Setup'}
        </button>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
      <Navbar />
      <AuthStateHandler />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/browse" element={<Browse />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/login" element={<Login />} />
        <Route path="/post" element={<PostListing />} />
        <Route path="/listing/:id" element={<ListingDetail />} />
        <Route path="/my-listings" element={<MyListings />} />
        <Route path="/edit-listing/:id" element={<EditListing />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/confirm-email" element={<ConfirmEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/saved" element={<SavedListings />} />
        <Route path="/complete-profile" element={<CompleteProfile />} />
      </Routes>
      <footer style={styles.footer}>
        <p style={{margin: 0}}>
          © 2026 SchoolYardSale &nbsp;·&nbsp;
          <Link to="/terms" style={styles.footerLink}>Terms of Service</Link>
          &nbsp;·&nbsp;
          <Link to="/privacy" style={styles.footerLink}>Privacy Policy</Link>
          &nbsp;·&nbsp;
          <a href="mailto:hello@schoolyardsale.com" style={styles.footerLink}>Contact Us</a>
        </p>
      </footer>
      </AuthProvider>
    </Router>
  );
}

export default App;